import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import Button from "@mui/material/Button";
import { Dialog, DialogActions, DialogTitle } from "@material-ui/core";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { MenuItem } from "@mui/material";
import moment from "moment";
import personDetailsService from "../../core/services/personDetailsService";
import bookingplanService from "../../core/services/bookingplanService";
import {
  DOCTOR_LEAVE_EARLY_TIMINGS,
  APPT_MORNING_TIMINGS,
  APPT_EVENING_TIMINGS,
  Docter_leave_start_time,
  Docter_leave_end_time,
  Docter_leave_unavailable_slot_value,
  getOrgConstant,
  ORG_CODES,
} from "../../shared/constants";
import {
  doctorActions,
  patientActions,
  tokenActions,
} from "../../core/actions";
import { helperServices } from "../../shared/helper";
import Select, { createFilter } from "react-select";
import { useHistory } from "react-router-dom";
import Components from "./index";
import "./DoctorCalendar.css";
import { useOrganization } from "../../configurations/OrganizationContext";

const formatCalendarTime = (value) => {
  const [rawHour, rawMinute = "00"] = String(value).split(":");
  const hour = Number(rawHour);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${rawMinute} ${suffix}`;
};

const addCalendarMinutes = (value, minutesToAdd) => {
  const [rawHour, rawMinute = "00"] = String(value).split(":");
  const totalMinutes = Number(rawHour) * 60 + Number(rawMinute) + minutesToAdd;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// Builds the 24 hourly cards from the configured 15-minute leave slots.
const buildHourlyCalendarSlots = () => {
  const intervalsByHour = {};

  [
    ...DOCTOR_LEAVE_EARLY_TIMINGS,
    ...APPT_MORNING_TIMINGS,
    ...APPT_EVENING_TIMINGS,
  ].forEach((row) => {
    [1, 2, 3, 4].forEach((index) => {
      const time = row[`time${index}`];
      const type = row[`type${index}`];
      if (!time || !type) return;

      const normalizedTime = `${String(Number(time.split(":")[0])).padStart(
        2,
        "0"
      )}:${time.split(":")[1]}`;
      const hour = Number(normalizedTime.split(":")[0]);
      if (!intervalsByHour[hour]) intervalsByHour[hour] = [];
      intervalsByHour[hour].push({
        type,
        time,
        start: normalizedTime,
        end: addCalendarMinutes(normalizedTime, 15),
        supported: true,
      });
    });
  });

  return Array.from({ length: 24 }, (_, hour) => {
    const start = `${String(hour).padStart(2, "0")}:00`;
    const end =
      hour === 23 ? "23:59" : `${String(hour + 1).padStart(2, "0")}:00`;
    const syntheticType = `calendar_${start.replace(":", "_")}`;
    return {
      start,
      end,
      intervals: intervalsByHour[hour] || [
        {
          start,
          end,
          type: syntheticType,
          time: `${hour}:00`,
          supported: true,
          synthetic: true,
        },
      ],
    };
  });
};

const HOURLY_CALENDAR_SLOTS = buildHourlyCalendarSlots();
const LEAVE_TIME_SLOT_LIST = [
  ...DOCTOR_LEAVE_EARLY_TIMINGS,
  ...APPT_MORNING_TIMINGS,
  ...APPT_EVENING_TIMINGS,
];
const LEAVE_SLOT_TYPES = LEAVE_TIME_SLOT_LIST.flatMap((row) =>
  [1, 2, 3, 4]
    .map((index) => row[`type${index}`])
    .filter(Boolean)
);
const LEAVE_HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0")
);
const LEAVE_MINUTE_OPTIONS = ["00", "15", "30", "45"];
const LEAVE_PERIOD_OPTIONS = ["AM", "PM"];

// Splits an internal 24-hour value into hour, minute, and AM/PM for the UI.
const getCalendarTimeParts = (value) => {
  const [hour = "8", minute = "00"] = String(value || "8:00").split(":");
  const numericHour = Number(hour);
  const normalizedHour = numericHour === 24 ? 0 : numericHour;
  return {
    hour: String(normalizedHour % 12 || 12).padStart(2, "0"),
    minute: minute.padStart(2, "0"),
    period: normalizedHour >= 12 ? "PM" : "AM",
  };
};

// Converts the separate 12-hour inputs back to the 24-hour value used by leave logic.
const getCalendar24HourValue = (
  hour,
  minute,
  period,
  isEndTime,
  endOfDayValue
) => {
  const numericHour = Number(hour);
  const endOfDay = isEndTime && period === "AM" && numericHour === 12;

  if (endOfDay) return endOfDayValue;

  const hour24 =
    period === "PM" ? (numericHour % 12) + 12 : numericHour % 12;
  return `${hour24}:${minute}`;
};

const getCalendarDateLabel = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : format(date, "dd MMM, yyyy");
};

const getCalendarDateValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : format(date, "yyyy-MM-dd");
};

const calendarTimeToMinutes = (value) => {
  if (value == null || value === "") return null;
  const [rawHour, rawMinute = "00"] = String(value).split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const calendarTimesOverlap = (startA, endA, startB, endB) => {
  const startAMinutes = calendarTimeToMinutes(startA);
  const endAMinutes = calendarTimeToMinutes(endA);
  const startBMinutes = calendarTimeToMinutes(startB);
  const endBMinutes = calendarTimeToMinutes(endB);

  if (
    startAMinutes == null ||
    endAMinutes == null ||
    startBMinutes == null ||
    endBMinutes == null
  ) {
    return false;
  }

  return startAMinutes < endBMinutes && startBMinutes < endAMinutes;
};

const mergeCalendarIntervals = (intervals) =>
  intervals.reduce((groups, interval) => {
    const previousGroup = groups[groups.length - 1];
    if (
      previousGroup &&
      previousGroup.status === interval.status &&
      previousGroup.end === interval.start
    ) {
      previousGroup.end = interval.end;
      previousGroup.intervals.push(interval);
      return groups;
    }

    groups.push({
      status: interval.status,
      start: interval.start,
      end: interval.end,
      intervals: [interval],
    });
    return groups;
  }, []);

function AppointmentScheduleModal(props) {
  console.log(props, "schedulemodallc");
  const org = useOrganization();
  const doctorLeaveStartTime = getOrgConstant(
    Docter_leave_start_time,
    org?.orgCode
  );
  const doctorLeaveEndTime = getOrgConstant(
    Docter_leave_end_time,
    org?.orgCode
  );
  const doctorLeaveUnavailableSlotValue = getOrgConstant(
    Docter_leave_unavailable_slot_value,
    org?.orgCode
  );
  const scheduleScrollRef = useRef(null);
  const eightAmSlotRef = useRef(null);
  const [requestedtime, setRequestedtime] = useState({ time: "" });
  const [addNewAppmnt, setAddNewAppmnt] = useState(false);
  const [currentWeek, setCurrentWeek] = useState([]);
  const [defaultweekDays, setDefaultweekDays] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [selecetdDate, setSelecetdDate] = useState("");
  const [fullDayEndDate, setFullDayEndDate] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const [leaveSolts, setLeaveSolts] = useState([]);
  const [isFullDayUnavailable, setIsFullDayUnavailable] = useState(false);
  const [reloadModal, setReloadModal] = useState(false);
  const [timeSelected, setTimeSelected] = useState();
  var startDate = "";
  var endDate = "";
  var docCalender;
  const [onmodal, setOnModal] = useState(true);
  const [appointmentDetail, setappointmentDetail] = useState({
    reqtime: "",
    reqtdate: "",
    reqthours: "",
    reqtminutes: "",
    apptdate: "",
    appthours: "",
    apptminutes: "",
    type: "Doctor",
    counselor: "",
    patientId: "",
    visitId: "",
    vaId: "",
    v: "",
    va: "",
    gvid: "",
    calField: "",
    timeslots: [],
    c: {},
    departmentId: "",
  });
  const [visitdetail, setVisitdetail] = useState({
    patientId: "",
    reqtime: "",
    reqtdate: "",
    reqthours: "",
    reqtminutes: "",
    counselor: "",
    apptdate: "",
    appthours: "",
    apptminutes: "",
    type: "Doctor",
    vaId: "",
    status: "",
    reId: "",
    gvid: "",
    calField: "",
    patientType: "OutPatient",
    timeslots: [],
    c: {},
  });
  const [pDetails, setPDetails] = useState({
    reqtime: "",
    reqtdate: "",
    reqthours: "",
    reqtminutes: "",
    type: "Doctor",
    counselor: "",
    patientId: "",
    visitId: "",
    vaId: "",
    v: "",
    va: {},
    Patient: {},
  });
  const dispatch = useDispatch();
  const [doctorList, setdoctorList] = useState([]);
  const [flag, setFlag] = useState({});
  const [appBooked, setAppBooked] = useState(false);
  const [status, setStatus] = useState(undefined);
  const [ServerRes, setServerRes] = useState({ success: "", error: "" });
  const [filteredTimeSlot, setfilteredTimeSlot] = useState({});
  const [updatedTimeSolt, setUpdatedTimeSolt] = useState([]);
  const [showAppointedPatients, setShowAppointedPatients] = useState(false);
  const [flagStatus, setFlagStaus] = useState(false);

  const [doctorDetails, setDoctorDetails] = useState({
    gvid: "",
    date: "",
    appthours: "",
    apptminutes: "",
    patientId: "",
  });

  const [reloadData, setreloadData] = useState(false);
  const [calender, setCalender] = useState({});
  const [getCal, setGetCal] = useState({});
  const [doctorCalender, setDoctorCalender] = useState({});
  const [formErrors, setFormErrors] = useState({
    counselor: "",
    apptdate: "",
    type: "",
  });
  const [timeslotsFromDb, setTimeslotsFromDb] = useState([]);
  const [selectedDoctor, setselectedDoctor] = useState([]);
  const [calendarDoctorId, setCalendarDoctorId] = useState("");
  const [bookingScheduleDoctorId, setBookingScheduleDoctorId] = useState("");
  const [bookingPlanSchedule, setBookingPlanSchedule] = useState([]);
  const calendarRequestIdRef = useRef(0);
  const getResolvedCalendarDoctorId = () => {
    if (props.doctorHomeView) {
      return calendarDoctorId || props?.doctorInfo?.id || "";
    }

    return (
      calendarDoctorId ||
      appointmentDetail.counselor ||
      visitdetail.counselor ||
      props?.doctorInfo?.gvId ||
      props?.doctorInfo?.gvid ||
      props?.CounsellorInfo?.gvId ||
      props?.selectedCousellerInfo?.gvId ||
      ""
    );
  };
  const [bookedCountAtTime, setBookedCountAtTime] = useState({});
  const [bookedPatientSlots, setBookedPatientSlots] = useState([]);
  const [toCancelSlot, setToCancelSlot] = useState({});
  const [appointedPatient, setAppointedPatient] = useState([]);
  const currentTime = format(new Date(), "HH:mm");
  const [fromTime, setFromTime] = useState(
    doctorLeaveStartTime
  );
  const [toTime, setToTime] = useState(
    doctorLeaveEndTime
  );
  const [selectedDateForValidation, setSelectedDateForValidation] = useState(
    {}
  );

  const [selectedRadio, setSelectedRadio] = useState("OutPatient");
  const [selectedDay, setSelectedDay] = useState("FullDay");

  // Add state for current time highlighting
  const [highlightedCurrentSlot, setHighlightedCurrentSlot] = useState(null);
  const [autoSelectedSlot, setAutoSelectedSlot] = useState(null);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationType, setConfirmationType] = useState(""); // 'doctorChange' or 'timeslotChange'
  const [pendingAppointment, setPendingAppointment] = useState(null);
  const [existingAppointmentInfo, setExistingAppointmentInfo] = useState(null);

  console.log("CURRENT TIMUU", currentTime);

  useEffect(() => {
    setselectedDoctor("");
    setTimeSlots([]);
    setBookedPatientSlots([]);
    setappointmentDetail((prev) => ({
      ...prev,
      counselor: "",
      gvid: "",
      appthours: "",
      apptminutes: "",
      apptdate: "",
      timeslots: [],
    }));
    setVisitdetail((prev) => ({
      ...prev,
      counselor: "",
      gvid: "",
      appthours: "",
      apptminutes: "",
      apptdate: "",
      timeslots: [],
    }));
    setFormErrors({
      counselor: "",
      apptdate: "",
      type: "",
    });
    setOnModal(true);

    // FIRST: Get week days
    getWeekDays();

    // THEN: Set current date and highlight it
    const currentDate = format(new Date(), "yyyy-MM-dd");
    setSelectedDateForValidation(currentDate);
    setSelecetdDate(currentDate); // ADD this line to highlight current date

    // Set appointment dates to current date
    setappointmentDetail((prev) => ({
      ...prev,
      apptdate: currentDate,
    }));
    setVisitdetail((prev) => ({
      ...prev,
      apptdate: currentDate,
    }));

    getDoctorsList();
    defaultDateSelection(); // This should now properly highlight current date

    console.log("-------APPOINTMENT MODAL-------", props);
    console.log("setSelectedDateForValidation", currentDate);

    if (!props.doctorHomeView) {
      setFlag(props.patientData.typeFlag);
      if (props.patientData.visit || props.patientData.revisit) {
        scheduleDoctorAppointment();
        getDoctorsList();
      }
    }
  }, [props.patientData]);

  useEffect(() => {
    updateCurrentTimeHighlighting();
  }, [appointmentDetail.counselor, bookedPatientSlots, selecetdDate]);

  useEffect(() => {
    if (defaultweekDays.length > 0 && !selecetdDate) {
      const currentDate = format(new Date(), "yyyy-MM-dd");
      if (defaultweekDays.includes(currentDate)) {
        console.log("Setting current date as selected after week is loaded");
        setSelecetdDate(currentDate);
      }
    }
  }, [defaultweekDays]);

  useEffect(() => {
    const selectedDateValue = getCalendarDateValue(selecetdDate);
    if (selectedDateValue) {
      setFullDayEndDate(selectedDateValue);
    }
  }, [selecetdDate]);

  useEffect(() => {
    if (
      !onmodal ||
      !props.doctorHomeView ||
      !selecetdDate ||
      formErrors.counselor
    ) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const scrollContainer = scheduleScrollRef.current;
      const eightAmSlot = eightAmSlotRef.current;
      if (!scrollContainer || !eightAmSlot) return;

      const containerTop = scrollContainer.getBoundingClientRect().top;
      const slotTop = eightAmSlot.getBoundingClientRect().top;
      scrollContainer.scrollTop += slotTop - containerTop;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    onmodal,
    props.doctorHomeView,
    selecetdDate,
    formErrors.counselor,
  ]);

  // Function to check if we should highlight current time slot
  const shouldHighlightCurrentTime = () => {
    // Highlight when there are no existing patient bookings for the day
    // and the selected date is today. Do NOT require a doctor to be
    // selected – the UI should hint the current slot even if user
    // hasn't picked a doctor yet.
    const hasNoExistingAppointment =
      !bookedPatientSlots || bookedPatientSlots.length === 0;
    const isToday = selecetdDate === format(new Date(), "yyyy-MM-dd");

    return hasNoExistingAppointment && isToday;
  };

  // Function to update current time highlighting
  const updateCurrentTimeHighlighting = () => {
    if (shouldHighlightCurrentTime()) {
      const nextSlot = getNextAvailableTimeSlot();
      setHighlightedCurrentSlot(nextSlot ? nextSlot.type : null);
      console.log("Highlighting current time slot:", nextSlot);
      console.log(
        "Patient has no existing appointment and doctor is selected - highlighting current time"
      );
    } else {
      setHighlightedCurrentSlot(null);
      console.log("Not highlighting - conditions not met:", {
        hasDoctor: appointmentDetail.counselor !== "",
        hasNoExistingAppointment:
          !bookedPatientSlots || bookedPatientSlots.length === 0,
        isToday: selecetdDate === format(new Date(), "yyyy-MM-dd"),
      });
    }
  };

  const getNextAvailableTimeSlot = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // Define time boundaries (8:00 AM = 480 minutes, 8:45 PM = 1245 minutes)
    const startTime = 8 * 60; // 8:00 AM in minutes
    const endTime = 20 * 60 + 45; // 8:45 PM in minutes

    console.log(
      "Current time in minutes:",
      currentTimeInMinutes,
      "Range:",
      startTime,
      "-",
      endTime
    );

    // If current time is before 8:00 AM, return 8:00 AM slot
    if (currentTimeInMinutes < startTime) {
      console.log("Before business hours, returning 8:00 AM slot");
      return findTimeSlotByTime("8:00");
    }

    // If current time is after 8:45 PM, don't highlight anything
    if (currentTimeInMinutes > endTime) {
      console.log("After business hours, no highlighting");
      return null;
    }

    // Find the next available 15-minute slot (round UP to next slot)
    let nextSlotMinutes = Math.ceil(currentTimeInMinutes / 15) * 15;

    // Convert back to hours and minutes
    const nextHour = Math.floor(nextSlotMinutes / 60);
    const nextMinute = nextSlotMinutes % 60;

    // Format time as H:mm (e.g., "8:00", "13:15")
    const nextTimeString = `${nextHour}:${nextMinute
      .toString()
      .padStart(2, "0")}`;

    console.log(
      "Current time:",
      `${currentHour}:${currentMinute.toString().padStart(2, "0")}`
    );
    console.log("Next available slot:", nextTimeString);

    // Find the corresponding timeslot type
    return findTimeSlotByTime(nextTimeString);
  };

  // 2. Function to find timeslot type by time string
  const findTimeSlotByTime = (timeString) => {
    // Search in morning timings
    for (let item of APPT_MORNING_TIMINGS) {
      if (item.time1 === timeString)
        return { time: timeString, type: item.type1, period: "morning" };
      if (item.time2 === timeString)
        return { time: timeString, type: item.type2, period: "morning" };
      if (item.time3 === timeString)
        return { time: timeString, type: item.type3, period: "morning" };
      if (item.time4 === timeString)
        return { time: timeString, type: item.type4, period: "morning" };
    }

    // Search in evening timings
    for (let item of APPT_EVENING_TIMINGS) {
      if (item.time1 === timeString)
        return { time: timeString, type: item.type1, period: "evening" };
      if (item.time2 === timeString)
        return { time: timeString, type: item.type2, period: "evening" };
      if (item.time3 === timeString)
        return { time: timeString, type: item.type3, period: "evening" };
      if (item.time4 === timeString)
        return { time: timeString, type: item.type4, period: "evening" };
    }

    return null;
  };

  // Helper: find time string by slot type (reverse lookup)
  const findTimeByType = (slotType) => {
    // Search morning timings rows
    for (let item of APPT_MORNING_TIMINGS) {
      if (item.type1 === slotType) return item.time1;
      if (item.type2 === slotType) return item.time2;
      if (item.type3 === slotType) return item.time3;
      if (item.type4 === slotType) return item.time4;
    }
    // Search evening timings rows
    for (let item of APPT_EVENING_TIMINGS) {
      if (item.type1 === slotType) return item.time1;
      if (item.type2 === slotType) return item.time2;
      if (item.type3 === slotType) return item.time3;
      if (item.type4 === slotType) return item.time4;
    }
    return null;
  };

  const scheduleDoctorAppointment = () => {
    let check = "";
    let patientVisit = props.patientData;
    if (props.patientData.typeFlag == "bookvisit") {
      // Schedule doctor appointment for followup patient
      setFlag(props.patientData.typeFlag);
      visitdetail.patientId = patientVisit.id;
      visitdetail.reqtdate = patientVisit.revisit.scheduledTime;
      visitdetail.status = "open";
      visitdetail.reId = patientVisit.revisit.id;
      visitdetail.apptdate = patientVisit.revisit.scheduleddate;
      defaultDateSelection();
      formatdatentime(patientVisit.revisit.scheduleddate);
      if (visitdetail.counselor != "") {
        getdoctorCalender(doctorCalender);
      }
    } else {
      // Schedule doctor appointment for new patient
      console.log("props.patientData", props.patientData.typeFlag);
      console.log("patientVisit", patientVisit);
      pDetails.visitId = patientVisit.visit.id;
      pDetails.v = patientVisit.visit;
      appointmentDetail.patientId = patientVisit.id;
      appointmentDetail.visitId = patientVisit.visit.id;
      appointmentDetail.v = patientVisit.visit;
      if (patientVisit.visit.reqtdate != null) {
        appointmentDetail.apptdate = patientVisit.visit.reqtdate;
        defaultDateSelection();
      } else {
        appointmentDetail.apptdate = patientVisit.visit.apptdate;
        defaultDateSelection();
      }
      for (let va of patientVisit.visitAssignmentList) {
        if (props.type.toLowerCase() === "doctor") {
          if (
            "Doctor" === va.type &&
            va.targetid != 0 &&
            va.status === "open"
          ) {
            pDetails.vaId = va.id;
            pDetails.va = va;
            appointmentDetail.va = va;
            appointmentDetail.vaId = va.id;
            appointmentDetail.counselor = va.targetid;
            appointmentDetail.gvid = va.targetid;
            check = false;
            break;
          }
        } else if (props.type == "Counsellor") {
          if (
            "Counsellor" === va.type &&
            va.targetid != 0 &&
            va.status === "open"
          ) {
            pDetails.vaId = va.id;
            pDetails.va = va;
            appointmentDetail.va = va;
            appointmentDetail.vaId = va.id;
            appointmentDetail.counselor = va.targetid;
            appointmentDetail.gvid = va.targetid;
            check = false;
            break;
          }
        }
      }
      if (check != "false") {
        appointmentDetail.va = patientVisit.visitAssignment;
        appointmentDetail.vaId = patientVisit.visitAssignment?.id ?? "";
      }
      formatdatentime(patientVisit.visit.reqtdate);
      if (appointmentDetail.counselor != "") {
        getdoctorCalender(doctorCalender);
      }

      if (props && props.doctorInfo) {
        if (props.doctorInfo.gvId === "") {
          console.log("Doctor API is not called");
        } else {
          getCal.apptdate = format(new Date(), "yyyy-MM-dd");
          getCal.gvid = props.doctorInfo.gvId;
          getCalDoc(getCal);
        }
      } else if (props && props.CounsellorInfo) {
        if (props.CounsellorInfo.gvId === "") {
          console.log("Counsellor API is not called");
        } else {
          getCal.apptdate = format(new Date(), "yyyy-MM-dd");
          getCal.gvid = props.CounsellorInfo.gvId;
          getCalDoc(getCal);
        }
      }
    }
  };

  const getDoctorsList = (data) => {
    let gvdata;
    const resCb = personDetailsService.getDoctorsDetail();
    // dispatch(
    //   doctorActions.getDoctorList(data, (resCb) => {
    if (resCb) {
      let list = [];
      console.log("rescb from getdoctorlist: ", resCb);
      for (let doc of resCb) {
        console.log("doc: ", doc);
        if (doc != null) {
          let opt = helperServices.buildDoctorOption(doc);
          list.push(opt);
          gvdata = props.doctorInfo || props.selectedCousellerInfo;
          console.log("props.doctorinfo: ", props);
          console.log("gvdata and doc: ", gvdata, doc);
          //TC_02
          const suppliedGvId = gvdata?.gvId ?? gvdata?.gvid;
          const suppliedPersonId = gvdata?.personId ?? gvdata?.id;
          const matchesDoctor = Boolean(
            gvdata &&
              ((suppliedGvId != null &&
                String(suppliedGvId) === String(doc.gvId)) ||
                (suppliedPersonId != null &&
                  String(suppliedPersonId) === String(doc.personId)) ||
                (gvdata.id != null &&
                  String(gvdata.id) === String(doc.gvId)))
          );
          if (matchesDoctor) {
            // Leave-calendar endpoints store and query records by gvid. The
            // person id is used only to identify the matching doctor above;
            // every calendar request must use the matched doctor's gvId.
            const calendarApiDoctorId = suppliedGvId ?? doc.gvId;
            setselectedDoctor(opt["label"]);
            console.log("selected doctor: ".selectedDoctor);
            setCalendarDoctorId(calendarApiDoctorId);
            setBookingScheduleDoctorId(doc.gvId);
            setappointmentDetail((previousDetails) => ({
              ...previousDetails,
              counselor: doc.gvId,
              gvid: doc.gvId,
              departmentId: doc.departmentId,
            }));

            if (props.doctorHomeView) {
              const initialDate = format(new Date(), "yyyy-MM-dd");
              getCalDoc({
                gvid: calendarApiDoctorId,
                bookingScheduleDoctorId: doc.gvId,
                apptdate: initialDate,
              });
            }
          }
        }
      }

      console.log("list of doctors:", list);
      console.log("propssss:", props.type);
      if (org?.orgCode !== ORG_CODES.VEDAMAAYU) {
        list = list.filter(
          (item) => item && item.type.toLowerCase() == props.type.toLowerCase()
        );
      }

     /* list = list.filter(
        (item) => item && item.type.toLowerCase() == props.type.toLowerCase()
      );
      console.log("list of doctors after filter:", list); */

      setdoctorList([...list]);
    }
    //   })
    // );
  };

  function removeCommaFromValues(obj) {
    const newObj = {};
    const newObj1 = {};
    for (let key in obj) {
      let ids = "";
      console.log(
        "key.split",
        obj[key],
        key,
        key.split(","),
        obj[key].split(",")
      );
      if (obj.hasOwnProperty(key)) {
        let data = obj[key].split(",");
        console.log("obj[key", obj[key]);
        for (let index = 0; index < data.length; index++) {
          console.log("data: ", data[index]);
          if (data[index] != "") {
            if (index != data.length - 1 && data[index + 1] != "") {
              ids = ids + data[index] + ",";
            } else {
              ids = ids + data[index];
            }
          }
        }
        console.log("ids", ids);
        newObj[key] = ids;
        console.log("ids", newObj);
      }
    }
    for (let key in newObj) {
      if (newObj[key] != "") {
        newObj1[key] = newObj[key];
      }
    }
    console.log("newObj1", newObj1);
    return newObj;
  }

  const getdoctorCalender = () => {
    if (flag != "bookvisit") {
      // getCal.apptdate = appointmentDetail.apptdate;
      getCal.apptdate =
        appointmentDetail.apptdate == format(new Date(), "yyyy-MM-dd")
          ? format(new Date(), "yyyy-MM-dd")
          : appointmentDetail.apptdate;

      getCal.gvid = appointmentDetail.counselor;
    } else {
      // getCal.apptdate = visitdetail.apptdate;
      getCal.apptdate =
        visitdetail.apptdate == format(new Date(), "yyyy-MM-dd")
          ? format(new Date(), "yyyy-MM-dd")
          : visitdetail.apptdate;

      getCal.gvid = visitdetail.counselor;
    }
    if (!getCal.apptdate || !getCal.gvid) return;
    const requestId = ++calendarRequestIdRef.current;
    setBookingPlanSchedule([]);
    bookingplanService
      .getDoctorSchedule(getCal.gvid, getCal.apptdate)
      .then((response) => {
        if (requestId !== calendarRequestIdRef.current) return;
        const schedule = response?.data?.data || response?.data || [];
        setBookingPlanSchedule(Array.isArray(schedule) ? schedule : []);
      })
      .catch((error) => {
        if (requestId !== calendarRequestIdRef.current) return;
        console.error("Failed to load booking-plan doctor schedule", error);
        setBookingPlanSchedule([]);
      });
    dispatch(
      patientActions.getDoctorCalenderByDate(getCal, (resCb) => {
        if (requestId !== calendarRequestIdRef.current) return;
        if (resCb) {
          console.log("foreacjhhfkvkfbkhvbkjrekuf", resCb);
          const filteredTimeSlot = {
            ...(resCb.c || resCb?.data?.c || resCb?.data || {}),
          };
          const keysToRemove = ["id", "gvid", "date"];
          keysToRemove.forEach((key) => delete filteredTimeSlot[key]);
          // console.log("filteredTimeSlot", filteredTimeSlot);
          // setfilteredTimeSlot(filteredTimeSlot);
          const updatedData = removeCommaFromValues(filteredTimeSlot);
          console.log(updatedData);
          setfilteredTimeSlot(updatedData);
          updatedTimeSolt.push(updatedData);
          let timeslotsFromDb = [];
          if (updatedData != null) {
            Object.keys(updatedData).forEach((key) => {
              if (
                updatedData[key] != null &&
                updatedData[key] != "" &&
                key != "gvid" &&
                key != "id"
              ) {
                timeslotsFromDb.push(key);
              }
            });
          }
          console.log("timeslotsFromDb", timeslotsFromDb);
          let bookedCountAtTimeloc = {};
          if (updatedData != null) {
            Object.entries(updatedData).map(([key, value]) => {
              if (
                updatedData[key] != null &&
                updatedData[key] != "" &&
                key != "gvid" &&
                key != "id" &&
                key != "date"
              ) {
                bookedCountAtTimeloc[key] = value.split(",").length;
                bookedCountAtTimeloc["pateintId"] = value.split(",");
              }
            });
          }
          setBookedCountAtTime(bookedCountAtTimeloc);

          // Compute slots booked by current patient only
          let bookedPatientSlot = [];
          const patientId = String(appointmentDetail.patientId);
          if (updatedData != null && patientId) {
            Object.entries(updatedData).forEach(([key, value]) => {
              if (
                value != null &&
                value !== "" &&
                key !== "gvid" &&
                key !== "id" &&
                key !== "date"
              ) {
                const ids = String(value)
                  .split(",")
                  .map((v) => v.trim());
                if (ids.includes(patientId)) {
                  bookedPatientSlot.push(key);
                }
              }
            });
          }
          if (flag != "bookvisit") {
            setBookedPatientSlots(bookedPatientSlot);
          }
          setDoctorCalender(timeslotsFromDb);
          setTimeslotsFromDb(timeslotsFromDb);

          // Update highlighting after calendar data is loaded
          setTimeout(() => {
            console.log("Triggering highlight after doctor calendar load");
            updateCurrentTimeHighlighting();
          }, 500);
        }
      })
    );
    getleaveCalender(getCal.gvid, getCal.apptdate, requestId);
  };

  const getCalDoc = (getCal) => {
    const requestId = ++calendarRequestIdRef.current;
    const doctorScheduleId =
      getCal.bookingScheduleDoctorId ||
      (!props.doctorHomeView ? getCal.gvid : "") ||
      bookingScheduleDoctorId ||
      props?.doctorInfo?.gvId ||
      props?.doctorInfo?.gvid ||
      getCal.gvid;
    setfilteredTimeSlot({});
    setBookedCountAtTime({});
    setBookedPatientSlots([]);
    setDoctorCalender([]);
    setTimeslotsFromDb([]);
    setLeaveSolts([]);
    setBookingPlanSchedule([]);

    if (doctorScheduleId && getCal.apptdate) {
      bookingplanService
        .getDoctorSchedule(doctorScheduleId, getCal.apptdate)
        .then((response) => {
          if (requestId !== calendarRequestIdRef.current) return;
          const schedule = response?.data?.data || response?.data || [];
          setBookingPlanSchedule(Array.isArray(schedule) ? schedule : []);
        })
        .catch((error) => {
          if (requestId !== calendarRequestIdRef.current) return;
          console.error("Failed to load booking-plan doctor schedule", error);
          setBookingPlanSchedule([]);
        });
    }

    dispatch(
      patientActions.getDoctorCalenderByDate(getCal, (resCb) => {
        if (requestId !== calendarRequestIdRef.current) return;
        if (resCb) {
          console.log("foreacjhhfkvkfbkhvbkjrekuf", resCb);
          const filteredTimeSlot = {
            ...(resCb.c || resCb?.data?.c || resCb?.data || {}),
          };
          const keysToRemove = ["id", "gvid", "date"];
          keysToRemove.forEach((key) => delete filteredTimeSlot[key]);
          console.log("filteredTimeSlot", filteredTimeSlot);
          const updatedData = removeCommaFromValues(filteredTimeSlot);
          console.log(updatedData);
          setfilteredTimeSlot(updatedData);
          updatedTimeSolt.push(updatedData);
          let timeslotsFromDb = [];
          if (updatedData != null) {
            Object.keys(updatedData).forEach((key) => {
              if (
                updatedData[key] != null &&
                updatedData[key] != "" &&
                key != "gvid" &&
                key != "id"
              ) {
                timeslotsFromDb.push(key);
              }
            });
          }
          console.log("timeslotsFromDb", timeslotsFromDb);
          let bookedCountAtTimeloc = {};
          if (updatedData != null) {
            Object.entries(updatedData).map(([key, value]) => {
              if (
                updatedData[key] != null &&
                updatedData[key] != "" &&
                key != "gvid" &&
                key != "id" &&
                key != "date"
              ) {
                bookedCountAtTimeloc[key] = value.split(",").length;
                bookedCountAtTimeloc["pateintId"] = value.split(",");
              }
            });
          }
          setBookedCountAtTime(bookedCountAtTimeloc);
          // Compute slots booked by current patient only
          let bookedPatientSlot = [];
          const patientId = String(appointmentDetail.patientId);
          if (updatedData != null && patientId) {
            Object.entries(updatedData).forEach(([key, value]) => {
              if (
                value != null &&
                value !== "" &&
                key !== "gvid" &&
                key !== "id" &&
                key !== "date"
              ) {
                const ids = String(value)
                  .split(",")
                  .map((v) => v.trim());
                if (ids.includes(patientId)) {
                  bookedPatientSlot.push(key);
                }
              }
            });
          }
          if (flag != "bookvisit") {
            setBookedPatientSlots(bookedPatientSlot);
          }
          setDoctorCalender(timeslotsFromDb);
          setTimeslotsFromDb(timeslotsFromDb);

          // Update highlighting after calendar data is loaded
          setTimeout(() => {
            console.log("Triggering highlight after doctor calendar load");
            updateCurrentTimeHighlighting();
          }, 500);
        }
        setTimeout(() => {
          console.log("Code executed after 2 seconds");
        }, 5000);
      })
    );
    getleaveCalender(getCal.gvid, getCal.apptdate, requestId);
  };
  const formatdatentime = (date) => {
    if (!date) return;
    const reqdate = new Date(date);
    let formatteddate = format(reqdate, "MMM dd yyyy' 'HH:mm");
    setRequestedtime({
      time: formatteddate,
    });
    return formatteddate;
  };

  const closeModal = () => {
    props.onModalClose();
  };

  // 1. MODIFY addNewAppointment function - Remove confirmation dialog logic
  const addNewAppointment = (time, type) => {
    const time1 = new Date();
    time1.setHours(time.split(":")[0]);
    time1.setMinutes(time.split(":")[1]);
    console.log("bookedPatientSlotshgghgggjyj", time1, time, type);

    let validation = validateField();
    console.log("validation", validation);

    if (validation == true) {
      // Handle doctor home view logic
      if (props.doctorHomeView) {
        const patientIds = filteredTimeSlot[type];
        if (patientIds) {
          dispatch(
            patientActions.getAppointedPatientDetails(patientIds, (resCb) => {
              if (resCb) {
                setAppointedPatient(resCb.patient);
                setShowAppointedPatients(true);
              }
            })
          );
          console.log("filteredTimeSlot", patientIds);
        } else {
          setAppointedPatient([]);
          setShowAppointedPatients(false);
          setTimeSlots((previousSlots) =>
            previousSlots.includes(type) ? [] : [type]
          );
        }
        return; // Exit early for doctor home view
      }

      setreloadData(!reloadData);

      let apptdateResult = validateFieldForApptDate(time1, type);
      if (!apptdateResult) {
        return;
      }

      // Clear current time highlighting when user manually selects a slot
      setHighlightedCurrentSlot(null);
      setAutoSelectedSlot(null);

      // Just handle the slot selection directly - no confirmation here
      handleDirectSlotChange(time, type);
    }
  };

  // NEW FUNCTION: Handle direct slot changes without confirmation
  const handleDirectSlotChange = (time, type) => {
    if (timeSlots.valueOf(0) == "") {
      appointmentDetail.appthours = time.split(":")[0];
      appointmentDetail.apptminutes = time.split(":")[1];
      visitdetail.appthours = time.split(":")[0];
      visitdetail.apptminutes = time.split(":")[1];
    }

    docCalender = calender;

    if (timeSlots.indexOf(type) > -1) {
      // If clicking the same slot, deselect it
      docCalender[type] = "";
      setCalender(docCalender);
      timeSlots.splice(timeSlots.indexOf(type), 1);
    } else {
      // Clear all previous selections first
      Object.keys(docCalender).forEach((key) => {
        if (docCalender[key] == props.patientData.id) {
          delete docCalender[key];
        }
      });

      // Clear the timeSlots array and add only the new selection
      timeSlots.length = 0; // Clear the array
      timeSlots.push(type); // Add the new slot

      docCalender[type] = props.patientData.id;
      setCalender(docCalender);

      // Update appointment times with the new selection
      appointmentDetail.appthours = time.split(":")[0];
      appointmentDetail.apptminutes = time.split(":")[1];
      visitdetail.appthours = time.split(":")[0];
      visitdetail.apptminutes = time.split(":")[1];
    }

    setreloadData(!reloadData);
    setTimeSlots([...timeSlots]);

    appointmentDetail.timeslots = timeSlots;
    appointmentDetail.c = docCalender;

    visitdetail.timeslots = timeSlots;
    visitdetail.c = docCalender;

    if (flag != "bookvisit") {
      setappointmentDetail({ ...appointmentDetail });
    } else {
      setVisitdetail({ ...visitdetail });
    }

    setAppBooked(true);
  };

  const getExistingAppointmentInfo = () => {
    const patientData = props.patientData;
    const visit = patientData.visit;

    let appointmentDate,
      appointmentHours,
      appointmentMinutes,
      existingDoctorName;

    // Get existing doctor info
    const existingDoctorId = getExistingDoctorId();
    const existingDoctor = doctorList.find(
      (doc) => doc.gvid === existingDoctorId || doc.id === existingDoctorId
    );
    existingDoctorName = existingDoctor
      ? existingDoctor.label
      : "Previously Assigned Doctor";

    // Get existing appointment details
    if (props.type.toLowerCase() === "doctor") {
      appointmentDate = visit.apptdate;
      appointmentHours = visit.appthours;
      appointmentMinutes = visit.apptminutes;
    } else {
      appointmentDate = visit.capptdate;
      appointmentHours = visit.cappthours;
      appointmentMinutes = visit.capptminutes;
    }

    // Get new appointment details
    const newDoctorName = selectedDoctor || "Selected Doctor";
    const newAppointmentDate = appointmentDetail.apptdate;
    const newAppointmentHours = appointmentDetail.appthours;
    const newAppointmentMinutes = appointmentDetail.apptminutes;

    return {
      patientName: patientData.fname,
      patientId: patientData.id,
      // Existing appointment
      existingDoctorName: existingDoctorName,
      existingAppointmentDate: appointmentDate,
      existingAppointmentHours: appointmentHours,
      existingAppointmentMinutes: appointmentMinutes,
      // New appointment
      newDoctorName: newDoctorName,
      newAppointmentDate: newAppointmentDate,
      newAppointmentHours: newAppointmentHours,
      newAppointmentMinutes: newAppointmentMinutes,
      // Conflict type
      isDocterChange: appointmentDetail.counselor !== getExistingDoctorId(),
      isTimeslotChange:
        bookedPatientSlots &&
        bookedPatientSlots.length > 0 &&
        timeSlots &&
        timeSlots.length > 0 &&
        !bookedPatientSlots.some((slot) => timeSlots.includes(slot)),
    };
  };

  // ADD: Debug function to help troubleshoot (you can remove this later)
  const debugAppointmentState = () => {
    console.log("=== APPOINTMENT DEBUG INFO ===");
    console.log("Props:", {
      type: props.type,
      doctorInfo: props.doctorInfo,
      CounsellorInfo: props.CounsellorInfo,
      selectedCousellerInfo: props.selectedCousellerInfo,
      patientData: props.patientData,
    });
    console.log("State:", {
      appointmentDetail,
      selectedDoctor,
      bookedPatientSlots,
      timeSlots,
    });
    console.log("Existing Doctor ID:", getExistingDoctorId());
    console.log("Current Doctor ID:", appointmentDetail.counselor);
    console.log("Has Existing Appointment:", checkForExistingAppointment());
    console.log("===============================");
  };

  // 6. MODIFY handleConfirmationAction to use the stored pending data
  const handleConfirmationAction = (action) => {
    if (action === "confirm") {
      // User confirmed to cancel & rebook
      cancelAndRebookAppointment();
    } else {
      // User cancelled - just close dialog
      setShowConfirmationDialog(false);
      setPendingAppointment(null);
      setExistingAppointmentInfo(null);
      setConfirmationType("");
    }
  };

  // FIXED: Properly cancel and rebook appointment
  const cancelAndRebookAppointment = () => {
    // Build cancellation using existing appointment info
    const existing = getExistingAppointmentDetails();
    const existingDoctorId = getExistingDoctorId();

    const toCancelSlot = {
      patientId: appointmentDetail.patientId,
      gvid: String(existingDoctorId || appointmentDetail.counselor || ""),
      date: existing.date || appointmentDetail.apptdate,
      vaid: getExistingVAId(),
      type: props.type,
    };

    console.log("Cancelling existing appointment:", toCancelSlot);

    dispatch(
      patientActions.cancelDoctorCalenderByPatientId(toCancelSlot, (resCb) => {
        if (resCb && resCb.successMessages) {
          console.log(
            "Existing appointment cancelled successfully, now booking new slot"
          );

          // Clear the booked patient slots since we cancelled
          setBookedPatientSlots([]);

          // Close confirmation dialog
          setShowConfirmationDialog(false);
          setPendingAppointment(null);
          setExistingAppointmentInfo(null);
          setConfirmationType("");

          // Proceed with the new appointment using current state
          setTimeout(() => {
            console.log("Proceeding with new appointment submission");
            proceedWithSubmission();
          }, 500);
        } else {
          console.error("Failed to cancel existing appointment:", resCb);
          setShowConfirmationDialog(false);
          // Show error message to user
          const errs = {
            ...formErrors,
            type: "Failed to cancel existing appointment. Please try again.",
          };
          setFormErrors(errs);
        }
      })
    );
  };

  // NEW FUNCTION: Submit new appointment after cancellation
  const submitNewAppointment = () => {
    let type = props.type;
    console.log("Submitting new appointment:", appointmentDetail);

    if (
      appointmentDetail.apptdate == null ||
      appointmentDetail.apptdate == undefined
    ) {
      appointmentDetail.apptdate = new Date();
    }

    appointmentDetail.va = props.patientData.visitAssignment;

    dispatch(
      patientActions.addAppointment(appointmentDetail, type, (resCb) => {
        if (resCb) {
          setStatus({ type: "success" });
          console.log("New appointment booked successfully");

          resCb["patientresp"] = props.patientData;
          props.onModalClose(resCb);

          tokenChange(
            appointmentDetail.visitId,
            appointmentDetail.gvid,
            appointmentDetail.type
          );
        }
      })
    );
  };

  const tokenChange = (visitId, gvId, type) => {
    // console.log("event in tokenchange method", event);
    if (
      props &&
      props.patientData &&
      props.patientData.visit &&
      props.patientData.visit.type == "OutPatient" &&
      props.patientData.visit.status == "open" &&
      props.type.toLowerCase() === "doctor"
    ) {
      if (
        props &&
        props.patientData &&
        props.patientData.tokenNumber &&
        parseInt(props.patientData.tokenNumber) > 0
      ) {
        dispatch(
          tokenActions.recorrectToken(visitId, gvId, (resCb) => {
            if (resCb) {
              console.log("token responses", resCb.token);
            }
          })
        );
      } else {
        dispatch(
          tokenActions.tokenGenerate(visitId, gvId, (resCb) => {
            if (resCb) {
              console.log("token responses", resCb.token);
            }
          })
        );
      }
    }
  };

  // FIXED submit function - Better re-assignment detection
  const submit = () => {
    validateField();
    var ServerResVal = ServerRes;
    console.log("selected doctor", selectedDoctor, props);
    console.log("Visit Details", visitdetail, appointmentDetail);
    let validation = validateField();
    console.log("validation", validation);

    if (validation == true) {
      // Enforce doctor selection before proceeding
      if (!appointmentDetail.counselor) {
        const errs = { ...formErrors, counselor: "Please Select Doctor" };
        setFormErrors(errs);
        return;
      }

      // NEW: Auto-assign highlighted current time slot if doctor selected but no timeslot
      if (highlightedCurrentSlot && (!timeSlots || timeSlots.length === 0)) {
        console.log(
          "Auto-assigning highlighted current time slot:",
          highlightedCurrentSlot
        );
        const nextSlot = getNextAvailableTimeSlot();
        if (nextSlot) {
          setAutoSelectedSlot(nextSlot.type);
          // Automatically select the highlighted slot
          handleDirectSlotChange(nextSlot.time, nextSlot.type);
          // Set appointment times
          appointmentDetail.appthours = nextSlot.time.split(":")[0];
          appointmentDetail.apptminutes = nextSlot.time.split(":")[1];
          visitdetail.appthours = nextSlot.time.split(":")[0];
          visitdetail.apptminutes = nextSlot.time.split(":")[1];
        }
      }

      // Enforce timeslot selection before proceeding (after auto-assignment)
      // if (!timeSlots || timeSlots.length === 0) {
      //   const errs = { ...formErrors, type: "Please select a timeslot." };
      //   setFormErrors(errs);
      //   return;
      // }
      if (flagStatus == false) {
        let currentDate = new Date();
        let curDate = format(currentDate, "yyyy-MM-dd");
        console.log("currentdate:", curDate);
        setSelecetdDate(curDate);
        appointmentDetail.apptdate = curDate;
        visitdetail.apptdate = curDate;
        setappointmentDetail(appointmentDetail);
        getdoctorCalender(doctorCalender);
      }

      //validateFieldForApptDate(true);

      if (appointmentDetail.appthours && appointmentDetail.apptminutes) {
        const selectedTime = new Date();
        selectedTime.setHours(appointmentDetail.appthours);
        selectedTime.setMinutes(appointmentDetail.apptminutes);
        validateFieldForApptDate(selectedTime, timeSlots[0]); // assuming timeSlots has selected slot
      }

      // ENHANCED: Better conflict detection
      const hasExistingAppointment = checkForExistingAppointment();
      const currentDoctorId = appointmentDetail.counselor;
      const existingDoctorId = getExistingDoctorId();

      // Additional check: see if this patient already has time slots booked
      const hasBookedSlots =
        bookedPatientSlots && bookedPatientSlots.length > 0;

      console.log("Detailed Conflict Check:", {
        hasExistingAppointment,
        hasBookedSlots,
        currentDoctorId,
        existingDoctorId,
        bookedPatientSlots,
        timeSlots,
        appointmentDetail,
      });

      // FIXED: Only show confirmation for actual re-assignments
      // if ((hasExistingAppointment || hasBookedSlots) && existingDoctorId && existingDoctorId !== "") {
      //   // Check for doctor change
      //   const isDifferentDoctor = currentDoctorId !== existingDoctorId;

      //   // Check for timeslot change (patient has booked slots but selecting different ones)
      //   const isDifferentTimeslot = hasBookedSlots &&
      //                              timeSlots && timeSlots.length > 0 &&
      //                              !bookedPatientSlots.some(slot => timeSlots.includes(slot));

      //   console.log("Change Detection:", {
      //     isDifferentDoctor,
      //     isDifferentTimeslot,
      //     existingDoctorId,
      //     currentDoctorId
      //   });

      //   if (isDifferentDoctor || isDifferentTimeslot) {
      //     // Show confirmation dialog for re-assignment
      //     console.log("Showing confirmation dialog for re-assignment");
      //     setExistingAppointmentInfo(getExistingAppointmentInfo());
      //     setPendingAppointment({
      //       appointmentDetail: {...appointmentDetail},
      //       visitdetail: {...visitdetail},
      //       timeSlots: [...timeSlots]
      //     });
      //     setConfirmationType(isDifferentDoctor ? 'doctorChange' : 'timeslotChange');
      //     setShowConfirmationDialog(true);
      //     return; // Don't proceed with submission yet
      //   }
      // }

      //same doctor with no appointment than set timeslot but dont show confirmation

      if (
        (hasExistingAppointment || hasBookedSlots) &&
        existingDoctorId &&
        existingDoctorId !== ""
      ) {
        const isDifferentDoctor = currentDoctorId !== existingDoctorId;
        const isDifferentTimeslot =
          hasBookedSlots &&
          timeSlots &&
          timeSlots.length > 0 &&
          !bookedPatientSlots.some((slot) => timeSlots.includes(slot));

        // 👉 Skip popup if same doctor and no existing timeslot
        const noTimeslotAssigned =
          !bookedPatientSlots || bookedPatientSlots.length === 0;

        if (isDifferentDoctor || (isDifferentTimeslot && !noTimeslotAssigned)) {
          setExistingAppointmentInfo(getExistingAppointmentInfo());
          setPendingAppointment({
            appointmentDetail: { ...appointmentDetail },
            visitdetail: { ...visitdetail },
            timeSlots: [...timeSlots],
          });
          setConfirmationType(
            isDifferentDoctor ? "doctorChange" : "timeslotChange"
          );
          setShowConfirmationDialog(true);
          return;
        }
      }

      // No conflicts or first-time assignment, proceed with normal submission
      console.log("Proceeding with direct submission - no conflicts detected");
      proceedWithSubmission();
    }
  };

  // FIXED: More precise check for existing appointments
  const checkForExistingAppointment = () => {
    const patientData = props.patientData;

    console.log("Checking for existing appointment:", {
      bookedPatientSlots,
      appointmentDetail,
      patientData,
    });

    // Primary check: bookedPatientSlots from calendar (most reliable)
    if (bookedPatientSlots && bookedPatientSlots.length > 0) {
      console.log(
        "Found existing appointment via bookedPatientSlots:",
        bookedPatientSlots
      );
      return true;
    }

    // Secondary check: Check if patient has actual appointment data in visit
    if (patientData && patientData.visit) {
      const visit = patientData.visit;
      if (props.type.toLowerCase() === "doctor") {
        // Check for existing doctor appointment with actual time data
        if (
          visit.apptdate &&
          visit.appthours !== null &&
          visit.appthours !== undefined &&
          visit.appthours !== "" &&
          visit.gvid &&
          visit.gvid !== ""
        ) {
          console.log("Found existing doctor appointment in visit:", {
            apptdate: visit.apptdate,
            appthours: visit.appthours,
            apptminutes: visit.apptminutes,
            gvid: visit.gvid,
          });
          return true;
        }
      } else if (props.type === "Counsellor") {
        // Check for existing counsellor appointment with actual time data
        if (
          visit.capptdate &&
          visit.cappthours !== null &&
          visit.cappthours !== undefined &&
          visit.cappthours !== "" &&
          visit.cgvid &&
          visit.cgvid !== ""
        ) {
          console.log("Found existing counsellor appointment in visit:", {
            capptdate: visit.capptdate,
            cappthours: visit.cappthours,
            capptminutes: visit.capptminutes,
            cgvid: visit.cgvid,
          });
          return true;
        }
      }
    }

    // Tertiary check: Check if appointmentDetail already has a doctor assigned with time
    if (
      appointmentDetail.counselor &&
      appointmentDetail.counselor !== "" &&
      appointmentDetail.appthours !== null &&
      appointmentDetail.appthours !== undefined &&
      appointmentDetail.appthours !== ""
    ) {
      console.log("Found existing appointment in appointmentDetail:", {
        counselor: appointmentDetail.counselor,
        appthours: appointmentDetail.appthours,
        apptminutes: appointmentDetail.apptminutes,
      });
      return true;
    }

    console.log("No existing appointment found");
    return false;
  };

  const getExistingDoctorId = () => {
    const patientData = props.patientData;

    // Method 1: Check from visitAssignmentList
    if (
      patientData.visitAssignmentList &&
      patientData.visitAssignmentList.length > 0
    ) {
      for (let va of patientData.visitAssignmentList) {
        if (
          props.type.toLowerCase() === "doctor" &&
          va.type.toLowerCase() === "doctor" &&
          va.targetid != 0 &&
          va.status === "open"
        ) {
          console.log(
            "Found existing doctor from visitAssignmentList:",
            va.targetid
          );
          return va.targetid;
        } else if (
          props.type === "Counsellor" &&
          "Counsellor" === va.type &&
          va.targetid != 0 &&
          va.status === "open"
        ) {
          console.log(
            "Found existing counsellor from visitAssignmentList:",
            va.targetid
          );
          return va.targetid;
        }
      }
    }

    // Method 2: Check from visitAssignment (single object)
    if (patientData.visitAssignment) {
      const va = patientData.visitAssignment;
      if (va.type === props.type && va.targetid != 0 && va.status === "open") {
        console.log("Found existing doctor from visitAssignment:", va.targetid);
        return va.targetid;
      }
    }

    // Method 3: Check from props (initial doctor selection)
    if (
      props.doctorInfo &&
      props.doctorInfo.gvId &&
      props.type.toLowerCase() === "doctor"
    ) {
      console.log(
        "Found existing doctor from props.doctorInfo:",
        props.doctorInfo.gvId
      );
      return props.doctorInfo.gvId;
    }

    if (
      props.CounsellorInfo &&
      props.CounsellorInfo.gvId &&
      props.type === "Counsellor"
    ) {
      console.log(
        "Found existing counsellor from props.CounsellorInfo:",
        props.CounsellorInfo.gvId
      );
      return props.CounsellorInfo.gvId;
    }

    // Method 4: Check from selectedCousellerInfo
    if (props.selectedCousellerInfo && props.selectedCousellerInfo.gvId) {
      console.log(
        "Found existing doctor from selectedCousellerInfo:",
        props.selectedCousellerInfo.gvId
      );
      return props.selectedCousellerInfo.gvId;
    }

    console.log("No existing doctor found");
    return null;
  };

  // Helper: Get existing Visit Assignment (VA) id for current type
  const getExistingVAId = () => {
    const patientData = props.patientData;
    if (patientData && Array.isArray(patientData.visitAssignmentList)) {
      for (let va of patientData.visitAssignmentList) {
        if (
          va &&
          va.type === props.type &&
          va.targetid != 0 &&
          va.status === "open"
        ) {
          return va.id;
        }
      }
    }
    // Fallbacks
    if (appointmentDetail && appointmentDetail.vaId)
      return appointmentDetail.vaId;
    if (
      patientData &&
      patientData.visitAssignment &&
      patientData.visitAssignment.id
    ) {
      return patientData.visitAssignment.id;
    }
    return null;
  };

  // Helper: Fetch existing appointment details (date/time/doctor)
  const getExistingAppointmentDetails = () => {
    const patientData = props.patientData;
    const visit = patientData && patientData.visit ? patientData.visit : {};
    if (props.type.toLowerCase() === "doctor") {
      return {
        date: visit.apptdate,
        hours: visit.appthours,
        minutes: visit.apptminutes,
        doctorId: getExistingDoctorId(),
      };
    } else {
      return {
        date: visit.capptdate,
        hours: visit.cappthours,
        minutes: visit.capptminutes,
        doctorId: getExistingDoctorId(),
      };
    }
  };

  // 4. NEW FUNCTION: Handle the actual submission logic
  const proceedWithSubmission = () => {
    if (props.patientData.typeFlag == "bookvisit") {
      console.log("visitdetail in revisit api", visitdetail);
      dispatch(
        patientActions.reAddVisit(visitdetail, (resCb) => {
          if (resCb) {
            setStatus({ type: "success" });
            props.onModalClose(resCb);
          }
        })
      );
    } else {
      let type = props.type;
      console.log("Appointment details is", appointmentDetail);

      if (
        appointmentDetail.apptdate == null ||
        appointmentDetail.apptdate == undefined
      ) {
        appointmentDetail.apptdate = new Date();
      }

      // **FIX: Ensure visitAssignment structure with proper fallbacks**
      let va = appointmentDetail.va || props.patientData.visitAssignment;

      if (!va) {
        // Create new VA structure if none exists
        va = {
          id: null,
          visitid: appointmentDetail.visitId,
          patientId: appointmentDetail.patientId,
          targetid: appointmentDetail.counselor,
          status: "open",
          type: type,
        };
      } else {
        // Update existing VA with new doctor
        va.targetid = appointmentDetail.counselor;
        va.type = type;
      }

      appointmentDetail.va = va;

      console.log("AppointmentDetails with complete VA-->", appointmentDetail);

      dispatch(
        patientActions.addAppointment(appointmentDetail, type, (resCb) => {
          if (resCb) {
            setStatus({ type: "success" });
            var ServerResVal = ServerRes;
            ServerResVal.success = "Appointment scheduled successfully.";
            resCb["patientresp"] = props.patientData;
            props.onModalClose(resCb);
            tokenChange(
              appointmentDetail.visitId,
              appointmentDetail.gvid,
              appointmentDetail.type
            );
          }
        })
      );
    }
  };

  const cancel = (data) => {
    toCancelSlot["patientId"] = appointmentDetail.patientId;
    toCancelSlot["gvid"] = appointmentDetail.gvid;

    if (appointmentDetail.apptdate == undefined) {
      toCancelSlot["date"] = appointmentDetail.v.capptdate;
      console.log("capptdate", appointmentDetail.v.capptdate);
    } else {
      toCancelSlot["date"] = appointmentDetail.apptdate;
    }
    //toCancelSlot["date"] = appointmentDetail.apptdate;
    toCancelSlot["vaid"] = appointmentDetail.vaId;
    toCancelSlot["type"] = props.type;
    console.log("toCancelSlot", toCancelSlot);
    var ServerResVal = ServerRes;
    dispatch(
      patientActions.cancelDoctorCalenderByPatientId(toCancelSlot, (resCb) => {
        if (resCb) {
          setStatus({ type: "success" });
          ServerResVal.success = "Appointment cancelled successfully.";
          resCb["patientresp"] = props.patientData;
          props.onModalClose(resCb);
          getdoctorCalender(doctorCalender);
          setFlagStaus(false);
        }
      })
    );
  };

  const getWeekDays = () => {
    let curr = new Date();
    let tempWeekDays = [];
    let tempDefaultWeekDays = [];
    let tempCurrentWeek = [];

    for (let i = 1; i <= 7; i++) {
      let first = curr.getDate() - curr.getDay() + i;
      let day = new Date(curr.setDate(first));
      let curDate = format(day, "yyyy-MM-dd");
      tempDefaultWeekDays.push(curDate);
      tempWeekDays.push(day);
      let formatteddate = format(day, "dd MMM");
      tempCurrentWeek.push(formatteddate);
    }

    // Set all states at once
    setDefaultweekDays(tempDefaultWeekDays);
    setWeekDays(tempWeekDays);
    setCurrentWeek(tempCurrentWeek);

    console.log("getWeekDays - current week includes:", tempDefaultWeekDays);
  };

  const validateFieldTimeSlots = () => {
    let currentDate = new Date();
    let curDate = format(currentDate, "yyyy-MM-dd");
    let fieldValidationErrors = formErrors;
    fieldValidationErrors.type = "";

    return true;
  };

  const validateFieldForApptDate = (selectedTimeslot, type) => {
    let currentDate = new Date();
    let curDate = format(currentDate, "yyyy-MM-dd");
    let currentTime = new Date();
    currentTime.setHours(currentDate.getHours());
    currentTime.setMinutes(currentDate.getMinutes());
    let fieldValidationErrors = formErrors;
    fieldValidationErrors.apptdate = "";
    console.log("appointment selecrted", selectedDateForValidation, curDate);
    console.log("timeSelectedtime", currentTime, timeSelected);
    let isDoctorUnavailable = Object.entries(leaveSolts).some(
      ([key, value]) => value === type
    );
    if (isDoctorUnavailable) {
      fieldValidationErrors.type =
        "Doctor is not available during the selected time";
      setFormErrors(fieldValidationErrors);
      return false;
    }
    if (selectedDateForValidation < curDate) {
      fieldValidationErrors.type =
        "You can not book an appointment for earlier date";
      setFormErrors(fieldValidationErrors);
      return false;
    } else if (selectedDateForValidation == curDate) {
      if (selectedTimeslot < currentTime) {
        console.log(
          "timeSelectedtime",
          currentTime,
          timeSelected,
          selectedTimeslot
        );
        fieldValidationErrors.type =
          "You can not book an appointment for earlier timeslot";
        setFormErrors(fieldValidationErrors);
        return false;
      }
      if (selectedDateForValidation == curDate) {
        //TC_01 - Do not block selection; show message but allow proceed
        if (bookedPatientSlots != "") {
          console.log("timeSelectedtime", currentTime, timeSelected);
          //fieldValidationErrors.type =
          //  "Patient already has appointment on different time slot. You may cancel it before assign new timeslot";
          //setFormErrors(fieldValidationErrors);
        }
      }
    }
    setFormErrors(fieldValidationErrors);
    return true;
  };

  const validateField = () => {
    let fieldValidationErrors = formErrors;
    fieldValidationErrors.counselor = "";
    if (flag != undefined || flag != "bookvisit") {
      if (appointmentDetail.counselor == "") {
        fieldValidationErrors.counselor = "Please Select Doctor";
        setFormErrors({ ...fieldValidationErrors });
        return false;
      }
      return true;
    } else {
      if (visitdetail.counselor == "") {
        fieldValidationErrors.counselor = "Please Select Doctor";
        setFormErrors(fieldValidationErrors);
        return false;
      }
      return true;
    }
  };
  // FIXED handleSelect function - Remove auto-highlighting for new patients
  const handleSelect = (event, date, appointment) => {
    //TC_04
    setLeaveSolts([]);
    setFlagStaus(true);
    console.log("Selected date", date, event, appointment);
    const gvid = getResolvedCalendarDoctorId();
    const appdate = new Date(date);
    const formattedAppointmentDate = format(appdate, "yyyy-MM-dd");

    setreloadData((previousValue) => !previousValue);
    validateField();
    validateFieldForApptDate(true);
    validateFieldTimeSlots(true);
    let counselorResult = validateField();
    if (!gvid || (!props.doctorHomeView && !counselorResult)) {
      return;
    }

    setSelecetdDate(date);
    setTimeSlots([]);
    setappointmentDetail((previousDetails) => ({
      ...previousDetails,
      apptdate: formattedAppointmentDate,
    }));
    setVisitdetail((previousDetails) => ({
      ...previousDetails,
      apptdate: formattedAppointmentDate,
    }));
    setSelectedDateForValidation(formattedAppointmentDate);

    // Load with the clicked date directly. Reading appointmentDetail here can
    // use the previous render's date because React state updates are async.
    getCalDoc({ gvid, apptdate: formattedAppointmentDate });

    // Update highlighting when date changes
    setTimeout(() => {
      updateCurrentTimeHighlighting();
    }, 500);
  };

  const nextSevenDays = () => {
    let formattedDays = [];
    let nonFormattedDays = [];
    const next7Days = [...Array(7).keys()].map((index) => {
      const date = new Date(weekDays[weekDays.length - 1]);
      date.setDate(date.getDate() + (index + 1));
      nonFormattedDays.push(date);
      let formatteddate = format(date, "dd MMM");
      formattedDays.push(formatteddate);
      return date;
    });

    setWeekDays(nonFormattedDays);
    setCurrentWeek(formattedDays);
  };

  const prevSevenDays = () => {
    let formattedDays = [];
    let nonFormattedDays = [];
    const past7Days = [...Array(7).keys()].map((index) => {
      const date = new Date(weekDays[0]);
      date.setDate(date.getDate() - (index + 1));
      nonFormattedDays.unshift(date);
      let formatteddate = format(date, "dd MMM");
      formattedDays.unshift(formatteddate);
      return date;
    });
    console.log("past7Days", past7Days);
    setWeekDays(nonFormattedDays);
    setCurrentWeek(formattedDays);
  };

  const defaultDateSelection = (inputDate) => {
    let formattedDays = [];
    let nonFormattedDays = [];
    let appdate;
    let currdate = new Date();
    const curdate = format(currdate, "yyyy-MM-dd");

    // Use current date by default, or provided inputDate
    appdate = inputDate || curdate;
    console.log("defaultDateSelection - using date:", appdate);

    if (appdate) {
      // Convert appdate to Date object for comparison
      const appdateObj = new Date(appdate);
      const curdateObj = new Date(curdate);

      // CASE 1: If appdate is within current week (defaultweekDays)
      if (
        defaultweekDays &&
        defaultweekDays.length > 0 &&
        defaultweekDays.includes(appdate)
      ) {
        console.log("Current date is in default week - highlighting it");

        // Convert defaultweekDays to Date objects
        const weekDateObjects = defaultweekDays.map(
          (dateStr) => new Date(dateStr)
        );
        const weekFormattedDays = defaultweekDays.map((dateStr) =>
          format(new Date(dateStr), "dd MMM")
        );

        setWeekDays(weekDateObjects);
        setCurrentWeek(weekFormattedDays);
        setSelecetdDate(appdate); // This will highlight the current date
      } else if (appdateObj > curdateObj) {
        // CASE 2: Future date - create week starting from that date
        console.log("Future date - creating week for:", appdate);
        const startDate = new Date(appdate);

        for (let i = 0; i < 7; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          nonFormattedDays.push(date);
          const defDate = format(date, "yyyy-MM-dd");
          let formatteddate = format(date, "dd MMM");
          formattedDays.push(formatteddate);
        }

        setWeekDays(nonFormattedDays);
        setCurrentWeek(formattedDays);
        setSelecetdDate(appdate);
      } else {
        // CASE 3: Past date or current date not in defaultweekDays
        console.log(
          "Past date or current date not in week - creating week for:",
          appdate
        );
        const startDate = new Date(appdate);

        for (let i = 0; i < 7; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          nonFormattedDays.push(date);
          const defDate = format(date, "yyyy-MM-dd");
          let formatteddate = format(date, "dd MMM");
          formattedDays.push(formatteddate);
        }

        setWeekDays(nonFormattedDays);
        setCurrentWeek(formattedDays);
        setSelecetdDate(appdate); // This ensures the date is highlighted

        // If doctor is selected, get calendar
        if (appointmentDetail.counselor) {
          getCal.apptdate = appdate;
          getCal.gvid = appointmentDetail.counselor;
          getCalDoc(getCal);
        }
      }
    }
  };

  const onAppointmentFormSubmit = () => {
    var pDetail = appointmentDetail;
    setappointmentDetail({ ...pDetail });
    setAddNewAppmnt(false);
    document.getElementById("appointment-body").style.display = "block";

    console.log("patientData:", props.patientData);
  };

  const onInputChange = (name, value) => {
    if (name == "title") {
      appointmentDetail.title = value;
    } else {
      appointmentDetail.description = value;
    }
    setappointmentDetail(appointmentDetail);
    console.log("appointmentDetail.title", appointmentDetail.title);
    console.log("appointmentDetail.desc", appointmentDetail);
  };

  const onAppointmentFormCancel = () => {
    setAddNewAppmnt(false);
    document.getElementById("appointment-body").style.display = "block";
  };

  // FIXED: Clear old bookings when doctor changes
  const handleDeptChange = (event) => {
    console.log("handleDeptChange event:", event);
    console.log("visitdetail", visitdetail);

    setselectedDoctor(event.label);
    setCalendarDoctorId(event.gvid);
    setBookingScheduleDoctorId(event.gvid);
    appointmentDetail.gvid = event.gvid;
    appointmentDetail.departmentId = event.departmentId;

    // ADDED: Clear previous doctor's booking data when changing doctor
    setBookedPatientSlots([]);
    setTimeSlots([]);
    setfilteredTimeSlot({});

    // Clear any auto-selected slot when doctor changes
    setAutoSelectedSlot(null);

    if (props.patientData.typeFlag == "bookvisit") {
      var docState = visitdetail;
      if (event != null) {
        docState["counselor"] = event.gvid;
        visitdetail.counselor = event.gvid;
        visitdetail.gvid = event.gvid;
        setselectedDoctor(event.label);
        getdoctorCalender(doctorCalender);
      }
      console.log("D-appointmentdetails", visitdetail);
    } else {
      var docState = appointmentDetail;
      if (event != null) {
        docState["counselor"] = event.gvid;
        appointmentDetail.counselor = event.gvid;
      }
      console.log("D-appointmentdetails", appointmentDetail);
      getleaveCalender(event.gvid, format(new Date(), "yyyy-MM-dd"));
      getdoctorCalender(doctorCalender);
      if (appointmentDetail.counselor === "") {
      } else {
        getCal.apptdate = format(new Date(), "yyyy-MM-dd");
        getCal.gvid = appointmentDetail.counselor;
        getCalDoc(getCal);
      }
    }
    validateField();

    // Update highlighting after doctor selection
    setTimeout(() => {
      updateCurrentTimeHighlighting();
    }, 500);

    // Debug call after doctor change
    setTimeout(() => {
      debugAppointmentState();
    }, 1000);
  };

  const PateintType = (value) => {
    console.log("selectedOptionRadio", value);
    setSelectedRadio(value);
    visitdetail["patientType"] = value;
  };

  // Resets all unavailable slots for every date in the selected date range.
  const handleMarkAvailable = () => {
    setLeaveSolts([]);
    setFormErrors([]);
    const gvid = getResolvedCalendarDoctorId();
    const date = getCalendarDateValue(selecetdDate);

    if (!fullDayEndDate || fullDayEndDate < date) {
      setFormErrors({
        ...formErrors,
        type: "Please select a valid Reset date range.",
      });
      return;
    }

    const resetDates = getFullDayDates(date, fullDayEndDate || date);
    const resetRequests = resetDates.map(
      (resetDate) =>
        new Promise((resolve, reject) => {
          dispatch(
            patientActions.updateleaveCalender(gvid, resetDate, (resCb) => {
              if (resCb?.successMessages) {
                resolve(resCb);
              } else {
                reject(resCb);
              }
            })
          );
        })
    );

    Promise.all(resetRequests)
      .then(() => {
        toggle();
        getleaveCalender(gvid, date);
      })
      .catch(() => {
        setFormErrors({
          ...formErrors,
          type: "Some selected dates could not be reset. Please try again.",
        });
      });
  };

  // Loads the doctor's latest leave slots and detects whether the whole day is unavailable.
  const getleaveCalender = (gvid, date, requestId) => {
    dispatch(
      patientActions.getleaveCalender(gvid, date, (resCb) => {
        if (
          requestId != null &&
          requestId !== calendarRequestIdRef.current
        ) {
          return;
        }
        const unavailableSlots = [];
        const leaveCalendar = resCb?.lc || resCb?.data?.lc || resCb?.data;
        const fullDayUnavailable =
          leaveCalendar != null &&
          LEAVE_SLOT_TYPES.length > 0 &&
          LEAVE_SLOT_TYPES.every(
            (slotType) =>
              leaveCalendar[slotType] ===
              doctorLeaveUnavailableSlotValue
          );

        setIsFullDayUnavailable(fullDayUnavailable);
        if (leaveCalendar != null) {
          Object.entries(leaveCalendar).forEach(([key, value]) => {
            if (value === doctorLeaveUnavailableSlotValue) {
              unavailableSlots.push(key);
            }
          });
        }
        setLeaveSolts(unavailableSlots);
      })
    );
    console.log("leaveSolts", leaveSolts, timeSlots);
  };

  // Sets the default date or time values when Full-Day, Half-Day, or Reset is selected.
  const onValueChange = (value) => {
    setSelectedDay(value);
    console.log("selectedOptionRadio", value);
    if (value === "FullDay") {
      setFromTime(doctorLeaveStartTime);
      setToTime(doctorLeaveEndTime);
      setFullDayEndDate(getCalendarDateValue(selecetdDate));
    }
    if (value === "reset") {
      setFormErrors([]);
      setFullDayEndDate(getCalendarDateValue(selecetdDate));
    }
    if (value === "HalfDay") {
      setFromTime("8:00");
      setToTime("8:00");
    }
    setFormErrors([]);
  };

  const handleOnChange = (value, event) => {
    setFormErrors([]);
    if (value === "fromTime") {
      setFromTime(event);
      console.log("Seleected Date is", event, event);
    }
    if (value === "toTime") {
      setToTime(event);
      console.log("Seleected Date is", event, event);
    }
  };

  // Combines the selected hour, minute, and AM/PM into one internal time value.
  const handleTimePartChange = (field, part, nextValue) => {
    const currentValue = field === "fromTime" ? fromTime : toTime;
    const currentParts = getCalendarTimeParts(currentValue);
    const hour = part === "hour" ? nextValue : currentParts.hour;
    let period = part === "period" ? nextValue : currentParts.period;
    if (
      part === "hour" &&
      period === "AM" &&
      isLeaveAmOptionDisabled(field, hour)
    ) {
      period = "PM";
    }
    const isEndTime = field === "toTime";
    const isEndOfDay = isEndTime && period === "AM" && hour === "12";
    const minute = isEndOfDay
      ? "00"
      : part === "minute"
      ? nextValue
      : currentParts.minute;

    handleOnChange(
      field,
      getCalendar24HourValue(
        hour,
        minute,
        period,
        isEndTime,
        doctorLeaveEndTime
      )
    );
  };

  const isLeaveAmOptionDisabled = () => false;

  const toggle = (event) => {
    setFormErrors([]);
    setSelectedDay("FullDay");
    setFromTime(doctorLeaveStartTime);
    setToTime(doctorLeaveEndTime);
    setFullDayEndDate(getCalendarDateValue(selecetdDate));
  };

  // Finds the database slot field that represents a selected 15-minute time.
  const findMatchingTime = (element, timeToFind) => {
    let matchedKey = null;
    let matchedIndex = -1;
    Object.keys(element).forEach((key, index) => {
      console.log(element[key], timeToFind);
      if (key.startsWith("time") && element[key] === timeToFind) {
        matchedKey = key;
        matchedIndex = parseInt(key.replace("time", "")) - 1;
      }
    });
    return { matchedKey, matchedIndex };
  };

  // Returns every calendar date between the selected From Date and To Date.
  const getFullDayDates = (startDate, endDate) => {
    if (!startDate || !endDate) return [];

    const dates = [];
    const currentDate = new Date(`${startDate}T00:00:00`);
    const lastDate = new Date(`${endDate}T00:00:00`);

    while (currentDate <= lastDate) {
      dates.push(format(currentDate, "yyyy-MM-dd"));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Validates the selection, builds the unavailable slots, and saves or resets the leave.
  const done = () => {
    setLeaveSolts([]);
    if (selectedDay === "reset") {
      handleMarkAvailable();
    } else {
      const selectedDateValue = getCalendarDateValue(selecetdDate);
      if (
        selectedDay === "FullDay" &&
        (!fullDayEndDate || fullDayEndDate < selectedDateValue)
      ) {
        setFormErrors({
          ...formErrors,
          type: "Please select a valid Full-Day date range.",
        });
        return;
      }

      let valid = validateLeaveTime(fromTime, toTime);
      if (valid) {
        let result = [];
        let resultsrray = [];
        var ServerResVal = ServerRes;
        console.log("time: type", fromTime, toTime);
        let startTime = moment(fromTime, "HH:mm");
        let endTime = moment(toTime, "HH:mm");
        let lcalender = {};
        while (startTime < endTime) {
          resultsrray.push(startTime.format("H:mm"));
          startTime.add(15, "minutes");
        }
        console.log("resultsrray", resultsrray);
        for (let index = 0; index < resultsrray.length; index++) {
          const time = resultsrray[index];
          console.log("resultsrray", time);
          let type = filterTimeSlot(time);
          if (type && type != "") {
            result.push(type);
          }
          console.log("result", result);
        }
        result.forEach((element) => {
          lcalender[element] = doctorLeaveUnavailableSlotValue;
        });
        const gvid = getResolvedCalendarDoctorId();
        const date = selectedDateValue;
        const leaveDates =
          selectedDay === "FullDay"
            ? getFullDayDates(date, fullDayEndDate || date)
            : [date];
        console.log("calender", lcalender, gvid);
        if (lcalender) {
          const saveRequests = leaveDates.map(
            (leaveDate) =>
              new Promise((resolve, reject) => {
                dispatch(
                  patientActions.leaveCalender(
                    lcalender,
                    gvid,
                    leaveDate,
                    (resCb) => {
                      if (resCb?.successMessages) {
                        resolve(resCb);
                      } else {
                        reject(resCb);
                      }
                    }
                  )
                );
              })
          );

          Promise.all(saveRequests)
            .then(() => {
              ServerResVal.success =
                leaveDates.length > 1
                  ? "Doctor unavailability added for all selected dates."
                  : "Not availability added successfully.";
              toggle();
              getleaveCalender(gvid, date);
            })
            .catch(() => {
              setFormErrors({
                ...formErrors,
                type:
                  "Some selected dates could not be saved. Please check and try again.",
              });
            });
        }
      } else {
        const gvid = getResolvedCalendarDoctorId();
        let date = format(selecetdDate, "yyyy-MM-dd");
        getleaveCalender(gvid, date);
        return;
      }
    }
  };

  // Converts a time such as 14:15 into its database field name.
  const filterTimeSlot = (time) => {
    let type;
    for (let index = 0; index < LEAVE_TIME_SLOT_LIST.length; index++) {
      type = "";
      const element = LEAVE_TIME_SLOT_LIST[index];
      const { matchedKey, matchedIndex } = findMatchingTime(element, time);
      if (matchedIndex > -1) {
        type = element[`type${matchedIndex + 1}`];
        break;
      }
    }
    return type;
  };

  // Checks that the selected leave range is valid and is not in the past.
  const validateLeaveTime = (time1, time2) => {
    let fieldValidationErrors = formErrors;
    let currentTime = new Date();
    const sTime = new Date(selecetdDate);
    if (time1) {
      sTime.setHours(time1.split(":")[0]);
      sTime.setMinutes(time1.split(":")[1]);
    }
    const eTime = new Date(selecetdDate);
    if (time2) {
      eTime.setHours(time2.split(":")[0]);
      eTime.setMinutes(time2.split(":")[1]);
    }
    console.log("validateLeaveTime", sTime, eTime, currentTime);

    if (currentTime >= sTime) {
      fieldValidationErrors.type = "You can not mark leave for earlier date";
      setFormErrors(fieldValidationErrors);
      return false;
    } else if (sTime >= eTime && time1 && time2) {
      fieldValidationErrors.type = "Please select a valid time range.";
      setFormErrors(fieldValidationErrors);
      return false;
    }

    return true;
  };

  const getCalendarIntervalStatus = (interval) => {
    if (
      isFullDayUnavailable ||
      leaveSolts.includes(interval.type)
    ) {
      return "unavailable";
    }
    if (
      Number(bookedCountAtTime[interval.type] || 0) > 0 ||
      bookedPatientSlots.includes(interval.type) ||
      bookingPlanSchedule.some((scheduledSession) =>
        calendarTimesOverlap(
          interval.start,
          interval.end,
          scheduledSession.start_time ?? scheduledSession.startTime,
          scheduledSession.end_time ?? scheduledSession.endTime
        )
      )
    ) {
      return "busy";
    }
    return "available";
  };

  const getCalendarStatusLabel = (status) => {
    if (status === "unavailable") return "NOT AVAILABLE";
    if (status === "busy") return "BUSY";
    return "FREE";
  };

  const selectCalendarInterval = (interval, status) => {
    if (status === "unavailable") return;
    addNewAppointment(interval.time, interval.type);
  };

  const handleHourlySlotAction = (hourlySlot) => {
    const intervals = hourlySlot.intervals.map((interval) => ({
      ...interval,
      status: getCalendarIntervalStatus(interval),
    }));
    const selectedInterval = intervals.find((interval) =>
      timeSlots.includes(interval.type)
    );
    const busyInterval = intervals.find(
      (interval) => interval.status === "busy"
    );
    const availableInterval = intervals.find(
      (interval) => interval.status === "available"
    );
    const selectableInterval = intervals.find(
      (interval) => interval.status !== "unavailable"
    );
    const interval = selectedInterval
      ? selectedInterval
      : props.doctorHomeView
      ? busyInterval || selectableInterval
      : availableInterval || selectableInterval;

    if (interval) selectCalendarInterval(interval, interval.status);
  };

  const selectedCalendarDateLabel = getCalendarDateLabel(selecetdDate);
  const selectedCalendarDateValue = getCalendarDateValue(selecetdDate);
  const fromTimeParts = getCalendarTimeParts(fromTime);
  const toTimeParts = getCalendarTimeParts(toTime);

  return (
    <>
      <Dialog
        className="appointment-modal "
        id="appointment-modal"
        open={onmodal}
        toggle={closeModal}
      >
        {flag == "bookvisit" ? (
          <DialogTitle className="py-2">
            Re-Book appointment
            <IconButton
              aria-label="close"
              onClick={(event) => closeModal(event)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        ) : (
          <>
            {props.doctorHomeView ? (
              <DialogTitle className="py-2">
                Calendar
                <IconButton
                  aria-label="close"
                  onClick={(event) => closeModal(event)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
            ) : (
              <DialogTitle className="py-2 mt-1">
                {/* Schedule Appointment */}
                Assign {props.type}
                <IconButton
                  aria-label="close"
                  onClick={(event) => closeModal(event)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
            )}
          </>
        )}
        <div className="d-flex">
          <div
            style={{
              flex: "0 1 80%",
              minWidth: 0,
              margin: showAppointedPatients ? 0 : "0 auto",
            }}
          >
            <div className="px-3 py-1 modal-subsection doctor-calendar-controls">
              <div className="d-flex flex-column">
                <div className="d-flex doctor-calendar-doctor-row">
                  <div className="w-50 border-right doctor-calendar-doctor-details">
                    <label className="label doctor-calendar-doctor-label">
                      {props.type}:
                    </label>
                    <div className="pr-4 py-0 w-100 doctor-calendar-doctor-name">
                      {props.attachOption || props.doctorHomeView ? (
                        <span className="d-flex flex-row">
                          {selectedDoctor}
                        </span>
                      ) : (
                        <Select
                          {...doctorList}
                          className="select-tag"
                          classNamePrefix="select-options"
                          name="Search Doctor"
                          value={doctorList.filter(
                            (option) => option.label === selectedDoctor
                          )}
                          onChange={handleDeptChange}
                          isClearable
                          isSearchable
                          options={doctorList}
                          filterOption={createFilter(doctorList)}
                          defaultValue={selectedDoctor}
                        ></Select>
                      )}
                    </div>
                    {!props.doctorHomeView && (
                      <div style={{ color: "#EC4147" }}>
                        <span className="err-msg">{formErrors.counselor}</span>
                      </div>
                    )}
                  </div>
                  {props.doctorHomeView === true ? (
                    ""
                  ) : (
                    <div className="w-50 ml-3 px-4">
                      <label className="label">Patient: </label>
                      <div>
                        <div style={{ marginTop: "-2px" }}>
                          <span>Name:&nbsp;</span>
                          <span style={{ textTransform: `capitalize` }}>
                            {props.patientData.fname}&nbsp;
                            {props.patientData.lname}
                          </span>
                        </div>
                        <div>
                          <span>Phone No:&nbsp;{props.patientData.phno}</span>
                        </div>
                        {props.patientData.typeFlag == "bookvisit" ? (
                          <>
                            {props.patientData.revisit.scheduleddate != null &&
                            props.patientData.revisit.scheduleddate ? (
                              <div>
                                <span>
                                  Requested Time:&nbsp;
                                  {props.patientData.revisit.scheduleddate}{" "}
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <>
                            {props.patientData.visit &&
                            props.patientData.visit.reqtdate &&
                            props.patientData.visit.reqtdate != null &&
                            props.patientData.visit.reqtdate ? (
                              <div>
                                <span>
                                  Requested Time:&nbsp;
                                  {props.patientData.visit.reqtdate}{" "}
                                  {props.patientData.visit.reqthours}:
                                  {props.patientData.visit.reqtminutes}
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedCalendarDateLabel && props.doctorHomeView && (
                  <section className="doctor-calendar-availability-panel">
                    <div className="doctor-calendar-availability-heading">
                      <div>
                        <h6>Set doctor unavailability</h6>
                        <span>
                          I am not available on{" "}
                          {selectedCalendarDateLabel}
                        </span>
                      </div>
                    </div>
                    <div className="doctor-calendar-availability-form">
                      <RadioGroup
                        row
                        aria-label="Doctor availability"
                        name="doctorAvailability"
                        value={selectedDay}
                        onChange={(event) =>
                          onValueChange(event.target.value)
                        }
                        className="doctor-calendar-availability-options"
                      >
                        <div className="doctor-calendar-availability-choice">
                          <FormControlLabel
                            value="FullDay"
                            control={<Radio />}
                            label="Full-Day"
                          />
                          {(selectedDay === "FullDay" ||
                            selectedDay === "reset") && (
                            <div className="doctor-calendar-full-day-range">
                            <label>
                              <span>From Date</span>
                              <input
                                type="date"
                                className="form-control"
                                value={selectedCalendarDateValue}
                                disabled
                              />
                            </label>
                            <label>
                              <span>To Date</span>
                              <input
                                type="date"
                                className="form-control"
                                min={selectedCalendarDateValue}
                                value={fullDayEndDate}
                                onChange={(event) => {
                                  setFormErrors([]);
                                  setFullDayEndDate(event.target.value);
                                }}
                              />
                            </label>
                            </div>
                          )}
                        </div>
                        <div className="doctor-calendar-availability-choice">
                          <FormControlLabel
                            value="HalfDay"
                            control={<Radio />}
                            label="Half-Day"
                          />
                          {selectedDay === "HalfDay" && (
                            <div className="doctor-calendar-availability-times">
                            <label>
                              <span>From</span>
                              <div className="doctor-calendar-time-parts">
                                <select
                                  className="form-control doctor-calendar-time-part"
                                  aria-label="From hour"
                                  value={fromTimeParts.hour}
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "fromTime",
                                      "hour",
                                      event.target.value
                                    )
                                  }
                                >
                                  {LEAVE_HOUR_OPTIONS.map((hour) => (
                                    <option
                                      key={`from-hour-${hour}`}
                                      value={hour}
                                    >
                                      {hour}
                                    </option>
                                  ))}
                                </select>
                                <strong aria-hidden="true">:</strong>
                                <select
                                  className="form-control doctor-calendar-time-part"
                                  aria-label="From minute"
                                  value={fromTimeParts.minute}
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "fromTime",
                                      "minute",
                                      event.target.value
                                    )
                                  }
                                >
                                  {LEAVE_MINUTE_OPTIONS.map((minute) => (
                                    <option
                                      key={`from-minute-${minute}`}
                                      value={minute}
                                    >
                                      {minute}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className="form-control doctor-calendar-time-part doctor-calendar-time-period"
                                  aria-label="From period"
                                  value={fromTimeParts.period}
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "fromTime",
                                      "period",
                                      event.target.value
                                    )
                                  }
                                >
                                  {LEAVE_PERIOD_OPTIONS.map((period) => (
                                    <option
                                      key={`from-period-${period}`}
                                      value={period}
                                      disabled={
                                        period === "AM" &&
                                        isLeaveAmOptionDisabled(
                                          "fromTime",
                                          fromTimeParts.hour
                                        )
                                      }
                                    >
                                      {period}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </label>
                            <label>
                              <span>To</span>
                              <div className="doctor-calendar-time-parts">
                                <select
                                  className="form-control doctor-calendar-time-part"
                                  aria-label="To hour"
                                  value={toTimeParts.hour}
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "toTime",
                                      "hour",
                                      event.target.value
                                    )
                                  }
                                >
                                  {LEAVE_HOUR_OPTIONS.map((hour) => (
                                    <option
                                      key={`to-hour-${hour}`}
                                      value={hour}
                                    >
                                      {hour}
                                    </option>
                                  ))}
                                </select>
                                <strong aria-hidden="true">:</strong>
                                <select
                                  className="form-control doctor-calendar-time-part"
                                  aria-label="To minute"
                                  value={toTimeParts.minute}
                                  disabled={
                                    toTimeParts.hour === "12" &&
                                    toTimeParts.period === "AM"
                                  }
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "toTime",
                                      "minute",
                                      event.target.value
                                    )
                                  }
                                >
                                  {(toTimeParts.hour === "12" &&
                                  toTimeParts.period === "AM"
                                    ? ["00"]
                                    : LEAVE_MINUTE_OPTIONS
                                  ).map((minute) => (
                                    <option
                                      key={`to-minute-${minute}`}
                                      value={minute}
                                    >
                                      {minute}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className="form-control doctor-calendar-time-part doctor-calendar-time-period"
                                  aria-label="To period"
                                  value={toTimeParts.period}
                                  onChange={(event) =>
                                    handleTimePartChange(
                                      "toTime",
                                      "period",
                                      event.target.value
                                    )
                                  }
                                >
                                  {LEAVE_PERIOD_OPTIONS.map((period) => (
                                    <option
                                      key={`to-period-${period}`}
                                      value={period}
                                      disabled={
                                        period === "AM" &&
                                        isLeaveAmOptionDisabled(
                                          "toTime",
                                          toTimeParts.hour
                                        )
                                      }
                                    >
                                      {period}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </label>
                            </div>
                          )}
                        </div>
                        <div className="doctor-calendar-availability-choice">
                          <FormControlLabel
                            value="reset"
                            control={<Radio />}
                            label="Reset"
                          />
                        </div>
                      </RadioGroup>
                      <div className="doctor-calendar-availability-actions">
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={toggle}
                          style={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={done}
                          style={{ textTransform: "none" }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                    {(formErrors.type || formErrors.apptdate) && (
                      <div className="doctor-calendar-availability-error">
                        {formErrors.type}
                        {formErrors.apptdate}
                      </div>
                    )}
                  </section>
                )}
                <div className="my-2 d-flex justify-content-between align-items-center doctor-calendar-dates">
                  <button
                    type="button"
                    className="btn doctor-calendar-dates__arrow"
                    onClick={(event) => prevSevenDays()}
                    aria-label="Show previous seven days"
                  >
                    <div className="material-icons arr-prev">
                      arrow_back_ios
                    </div>
                  </button>
                  {weekDays.map((item, idx) => (
                    <div
                      key={idx}
                      // className={
                      //   selecetdDate == item
                      //     ? "btn btn-date active"
                      //     : "btn btn-date"
                      // }

                      className={
                        format(new Date(selecetdDate), "yyyy-MM-dd") ===
                        format(new Date(item), "yyyy-MM-dd")
                          ? "btn btn-date active"
                          : "btn btn-date"
                      }
                      onClick={(event) =>
                        handleSelect(event, item, appointmentDetail)
                      }
                    >
                      {AppointmentScheduleModalFunction.getFormattedDate(item)}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn doctor-calendar-dates__arrow"
                    onClick={(event) => nextSevenDays()}
                    aria-label="Show next seven days"
                  >
                    <div className="material-icons arr-next">
                      arrow_forward_ios
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <DialogContent
              id="appointment-body"
              className={`py-1 justify-content-between${
                props.doctorHomeView ? " doctor-calendar-scroll" : " h-100"
              }`}
              ref={scheduleScrollRef}
            >
              <div className="doctor-calendar-schedule">
                {!formErrors.counselor ? (
                  <div className="doctor-calendar-grid">
                    {HOURLY_CALENDAR_SLOTS.map((hourlySlot) => {
                      const intervals = hourlySlot.intervals.map((interval) => ({
                        ...interval,
                        status: getCalendarIntervalStatus(interval),
                      }));
                      const intervalGroups = mergeCalendarIntervals(intervals);
                      const selected = intervals.some((interval) =>
                        timeSlots.includes(interval.type)
                      );
                      const selectable = intervals.some(
                        (interval) => interval.status !== "unavailable"
                      );
                      const hasBusyInterval = intervals.some(
                        (interval) => interval.status === "busy"
                      );
                      const actionLabel = selected
                        ? "SELECTED"
                        : !selectable
                        ? "NOT AVAILABLE"
                        : props.doctorHomeView && hasBusyInterval
                        ? "VIEW APPOINTMENTS"
                        : "SELECT SLOT";

                      return (
                        <div
                          className={`doctor-calendar-slot${
                            selected ? " doctor-calendar-slot--selected" : ""
                          }`}
                          key={hourlySlot.start}
                          ref={
                            hourlySlot.start === "08:00"
                              ? eightAmSlotRef
                              : null
                          }
                        >
                          <div className="doctor-calendar-slot__header">
                            {formatCalendarTime(hourlySlot.start)} -{" "}
                            {formatCalendarTime(hourlySlot.end)}
                          </div>
                          <div className="doctor-calendar-slot__segments">
                            {intervalGroups.map((group) => {
                              const selectedInterval = group.intervals.find(
                                (interval) => timeSlots.includes(interval.type)
                              );
                              const representativeInterval =
                                selectedInterval || group.intervals[0];
                              const isCurrent = group.intervals.some(
                                (interval) =>
                                  Boolean(interval.type) &&
                                  Boolean(highlightedCurrentSlot) &&
                                  interval.type === highlightedCurrentSlot
                              );
                              return (
                                <button
                                  type="button"
                                  key={`${group.start}-${group.end}-${group.status}`}
                                  className={`doctor-calendar-slot__segment doctor-calendar-slot__segment--${group.status}${
                                    isCurrent ? " doctor-calendar-slot__segment--current" : ""
                                  }`}
                                  style={{ flexGrow: group.intervals.length }}
                                  disabled={group.status === "unavailable"}
                                  onClick={() =>
                                    selectCalendarInterval(
                                      representativeInterval,
                                      group.status
                                    )
                                  }
                                  title={`${getCalendarStatusLabel(group.status)}: ${formatCalendarTime(
                                    group.start
                                  )} - ${formatCalendarTime(group.end)}`}
                                >
                                  <span className="doctor-calendar-slot__status">
                                    {getCalendarStatusLabel(group.status)}
                                  </span>
                                  <span className="doctor-calendar-slot__time">
                                    {formatCalendarTime(group.start)}-
                                    {formatCalendarTime(group.end)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            className={`doctor-calendar-slot__action${
                              selected ? " doctor-calendar-slot__action--selected" : ""
                            }`}
                            disabled={!selectable}
                            onClick={() => handleHourlySlotAction(hourlySlot)}
                          >
                            {actionLabel}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {!props.doctorHomeView && (
                  <div className="doctor-calendar-error">
                    <span className="err-msg">
                      {formErrors.type}
                      {formErrors.apptdate}
                    </span>
                  </div>
                )}

              </div>

              {addNewAppmnt && (
                <div className="flex-fill mt-4">
                  <div>
                    <div>
                      <div className="section-heading">
                        {appointmentDetail.title ? (
                          <h5>Edit Appointment</h5>
                        ) : (
                          <h5>Add New Appointment</h5>
                        )}
                      </div>
                      <form
                        className="has-background-white-ter px-4 py-3"
                        onSubmit={onAppointmentFormSubmit}
                      >
                        <div className="field my-4">
                          <label className="label">Title</label>
                          <div className="control">
                            <input
                              name="title"
                              className="input form-control"
                              type="text"
                              placeholder="Enter Title"
                              onChange={(event) =>
                                onInputChange("title", event.target.value)
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="field my-4">
                          <label className="label">Description</label>
                          <div className="control">
                            <textarea
                              name="description"
                              className="textarea form-control"
                              placeholder="Enter Description"
                              onChange={(event) =>
                                onInputChange("description", event.target.value)
                              }
                            ></textarea>
                          </div>
                          <p className="my-4">
                            <i className="has-text-weight-bold ">
                              Appointment Date:
                            </i>
                            <p>
                              {appointmentDetail.apptdate} &nbsp;{" "}
                              {appointmentDetail.appthours}:
                              {appointmentDetail.apptminutes}&nbsp;
                              {appointmentDetail.appthours > 11 ? "PM" : "AM"}
                            </p>
                          </p>
                        </div>
                        <div className="field is-grouped d-flex my-4">
                          <div className="control mr-3">
                            <button
                              type="submit"
                              className="btn appnt-button is-primary"
                            >
                              Save
                            </button>
                            <div style={{ color: "#EC4147" }}>
                              <span className="err-msg">
                                {formErrors.type}
                                {formErrors.apptdate}
                              </span>
                            </div>
                          </div>
                          <div className="control">
                            <button
                              type="button"
                              className="btn appnt-button btn-outline-dark "
                              onClick={onAppointmentFormCancel}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>

            <div
              className="doctor-calendar-legend"
              aria-label="Calendar status legend"
            >
              <span>
                <i className="doctor-calendar-legend__swatch doctor-calendar-legend__swatch--available" />
                Available
              </span>
              <span>
                <i className="doctor-calendar-legend__swatch doctor-calendar-legend__swatch--busy" />
                Scheduled
              </span>
              <span>
                <i className="doctor-calendar-legend__swatch doctor-calendar-legend__swatch--unavailable" />
                Not Available
              </span>
            </div>
          </div>

          {props.doctorHomeView === true && (
            <>
              {showAppointedPatients && (
                <div className="appointedPatient">
                  <label className="label">Appointed Patients:</label>
                  <div>
                    {appointedPatient.map((item) => (
                      <>
                        <div style={{ marginBottom: "5px" }}>
                          <span>Name:&nbsp;</span>
                          <span style={{ textTransform: `capitalize` }}>
                            {item.fname}&nbsp;{item.lname}
                          </span>
                          <div style={{ fontSize: "12px" }}>
                            Phone No:&nbsp;{item.phno}
                          </div>
                        </div>
                      </>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {
          <>
            {props.doctorHomeView ? (
              ""
            ) : (
              <DialogActions className="pt-3">
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: "capitalize" }}
                  onClick={(event) => cancel(toCancelSlot)}
                  //onClick={() => cancel()}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{ textTransform: "capitalize" }}
                  color="primary"
                  // disabled={!appBooked}
                  // className="btn btn-submit btn-primary btn-style bottomright"
                  onClick={(event) => submit(event)}
                >
                  Assign {props.type}
                </Button>
              </DialogActions>
            )}
            {showConfirmationDialog && (
              <Dialog
                open={showConfirmationDialog}
                onClose={() => handleConfirmationAction("cancel")}
                className="d-flex w-95 h-90 flex-column-reverse modalDialog"
              >
                <div>
                  <DialogTitle>
                    Confirmation
                    <IconButton
                      aria-label="close"
                      onClick={() => handleConfirmationAction("cancel")}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  {/* <DialogContent className="dealocate">
        <span>
          {existingAppointmentInfo && (
            <>
              {existingAppointmentInfo.patientName} ({existingAppointmentInfo.patientId}) already has
              an appointment with {existingAppointmentInfo.existingDoctorName} on{" "}
              {existingAppointmentInfo.existingAppointmentDate}{" "}
              {existingAppointmentInfo.existingAppointmentHours !== undefined && existingAppointmentInfo.existingAppointmentMinutes !== undefined
                ? `at ${existingAppointmentInfo.existingAppointmentHours}:${existingAppointmentInfo.existingAppointmentMinutes}${existingAppointmentInfo.existingAppointmentHours >= 12 ? " PM" : " AM"}`
                : ""}
              <br/><br/>
              Do you want to schedule a new appointment with {existingAppointmentInfo.newDoctorName} on{" "}
              {existingAppointmentInfo.newAppointmentDate}{" "}
              {existingAppointmentInfo.newAppointmentHours !== undefined && existingAppointmentInfo.newAppointmentMinutes !== undefined
                ? `at ${existingAppointmentInfo.newAppointmentHours}:${existingAppointmentInfo.newAppointmentMinutes}${existingAppointmentInfo.newAppointmentHours >= 12 ? " PM" : " AM"}`
                : ""}
              ?
            </>
          )}
        </span>
        <br />
        <span className="small">
          *If you continue, the existing appointment will be cancelled.*
        </span>
      </DialogContent> */}

                  <DialogContent className="dealocate">
                    <span>
                      {existingAppointmentInfo && (
                        <>
                          {existingAppointmentInfo.patientName} (
                          {existingAppointmentInfo.patientId}) already has an
                          appointment with{" "}
                          {existingAppointmentInfo.existingDoctorName} on{" "}
                          {existingAppointmentInfo.existingAppointmentDate}{" "}
                          {existingAppointmentInfo.existingAppointmentHours &&
                          existingAppointmentInfo.existingAppointmentMinutes &&
                          !(
                            existingAppointmentInfo.existingAppointmentHours ===
                              0 &&
                            existingAppointmentInfo.existingAppointmentMinutes ===
                              0
                          )
                            ? `at ${
                                existingAppointmentInfo.existingAppointmentHours
                              }:${existingAppointmentInfo.existingAppointmentMinutes
                                .toString()
                                .padStart(2, "0")}${
                                existingAppointmentInfo.existingAppointmentHours >=
                                12
                                  ? " PM"
                                  : " AM"
                              }`
                            : ""}
                          .
                          <br />
                          <br />
                          Do you want to schedule a new appointment with{" "}
                          {existingAppointmentInfo.newDoctorName} on{" "}
                          {existingAppointmentInfo.newAppointmentDate}{" "}
                          {existingAppointmentInfo.newAppointmentHours &&
                          existingAppointmentInfo.newAppointmentMinutes &&
                          !(
                            existingAppointmentInfo.newAppointmentHours === 0 &&
                            existingAppointmentInfo.newAppointmentMinutes === 0
                          )
                            ? `at ${
                                existingAppointmentInfo.newAppointmentHours
                              }:${existingAppointmentInfo.newAppointmentMinutes
                                .toString()
                                .padStart(2, "0")}${
                                existingAppointmentInfo.newAppointmentHours >=
                                12
                                  ? " PM"
                                  : " AM"
                              }`
                            : ""}
                          ?
                        </>
                      )}
                    </span>
                    <br />
                    <span className="small">
                      *If you continue, the existing appointment will be
                      cancelled.*
                    </span>
                  </DialogContent>

                  <DialogActions
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      className="btn-style bottomright"
                      onClick={() => handleConfirmationAction("cancel")}
                      style={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      className="btn-style bottomleft"
                      onClick={() => handleConfirmationAction("confirm")}
                      style={{ textTransform: "none" }}
                    >
                      Cancel & Book Appointment
                    </Button>
                  </DialogActions>
                </div>
              </Dialog>
            )}
            {/* <div
              className="d-flex modal-subsection pl-4 position-absolute"
              style={{ bottom: "10px" }}
            >
              <Components.TokenDashBoardDetails updateTokenDashboard={true} />
            </div> */}
          </>
        }
      </Dialog>
    </>
  );
}

export function getFormattedDate(date) {
  const reqdate = new Date(date);
  let formatteddate = format(reqdate, "dd MMM");
  return formatteddate;
}

const AppointmentScheduleModalFunction = {
  getFormattedDate,
};
export default AppointmentScheduleModal;
